import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { formatApiError } from "../lib/errors";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Search, ShieldAlert, AlertTriangle, CheckCircle2, XCircle, Loader2, Truck, Lock, RefreshCw,
  HelpCircle, Tag, ReceiptText,
} from "lucide-react";
import { toast } from "sonner";
import { InternalAdminLayout } from "./InternalAdmin";

const InternalShell = InternalAdminLayout;

// Same safety margin the Postman/runbook operator package already
// established for this exact workflow (dispatch_authorization_service.py's
// own source-enforced cap is 15 minutes / 60s-before-quote-expiry — this
// is this console's own, stricter, non-source-enforced recommendation on
// top of that, applied client-side).
const SAFE_REMAINING_MS = 3 * 60 * 1000;

const CREDENTIAL_ACK_TEXT =
  "I confirm I have checked the Uber Direct Dashboard and verified that this exact Client ID, " +
  "Client Secret, and Customer ID are classified there as Test mode credentials, not Production.";

const AUTHORIZATION_ACK_TEXT =
  "I authorize exactly one Uber Direct Create Delivery request in the TEST environment for the " +
  "specifically identified internal test order shown in the final preview. No retry is authorized.";

// Reconciliation-refund operator control (Aug 2026, UBE-ORD-000017
// incident-recovery follow-up). Must match `retail/delivery/dispatch/
// reconciliation_refund_service.py::RECONCILIATION_REFUND_CONFIRMATION_
// TEXT` EXACTLY — the backend's own confirm route rejects any body
// whose `acknowledgement_text` doesn't match this constant verbatim
// (`acknowledgement_mismatch`), so this string is not free-form copy,
// it is a real, server-checked value.
const RECONCILIATION_REFUND_CONFIRMATION_TEXT =
  "I authorize exactly one Stripe TEST refund for this specific stranded Local Delivery order, " +
  "using reason quote_expired_before_dispatch. No Uber delivery will be created. No retry is authorized.";
const RECONCILIATION_REFUND_REASON_CODE = "quote_expired_before_dispatch";

// ---------------------------------------------------------------------------
// Activation residue preview (Sep 2026) — READ-ONLY operator surface for
// `POST /api/internal-admin/uber-direct/residue-preview`, the dry run of
// the production-activation residue guards shipped in PR #155.
//
// This card exists here, on the tenant's own Uber console, rather than as
// a standalone page: it reuses the tenant-readiness gate above for
// SERVER-AUTHORITATIVE company identity, so an operator never types or
// pastes a company id. The catalog then comes from a server list scoped
// to that same company. Neither identifier is free text.
//
// Nothing in this card mutates anything. It renders no control that
// reconciles, activates, deletes, refunds or dispatches — a fact the
// tests assert against the rendered output, not just against intent.
// ---------------------------------------------------------------------------

//: The categories `production_activation._categorize` can emit, with the
//: operator-facing meaning of each. Kept in one place so the table below
//: and the tests agree; an unrecognised category renders as itself and is
//: treated as blocking, never as safe.
export const RESIDUE_CATEGORY_LABELS = {
  not_blocking: {
    label: "Non-blocking historical residue",
    detail: "Finished order, terminal delivery, no quote any consumer could still carry.",
    blocking: false,
  },
  requires_live_customer_decision: {
    label: "Reusable quote hazard",
    detail:
      "A quote some production consumer could still carry into checkout, payment or dispatch — "
      + "including an accepted quote the delivery no longer points at.",
    blocking: true,
  },
  sandbox_delivery_no_provider_id: {
    label: "In-flight delivery (never reached Uber)",
    detail: "Non-terminal delivery with no provider delivery id.",
    blocking: true,
  },
  requires_provider_resolution: {
    label: "Provider-backed delivery — requires provider truth",
    detail:
      "Non-terminal AND a provider delivery id exists. A courier object may exist at Uber. "
      + "Never closed automatically.",
    blocking: true,
  },
  requires_operator_payment_action: {
    label: "Unsettled payment — requires separate operator action",
    detail:
      "Money can still move on this order. Never change a payment state to satisfy an "
      + "activation.",
    blocking: true,
  },
  ambiguous_fail_closed: {
    label: "Ambiguous — fail-closed",
    detail: "The classifier could not positively explain this record. Investigate; it keeps blocking.",
    blocking: true,
  },
};

/**
 * Pure: is this preview a usable, complete inventory?
 *
 * A truncated scan is a STOP, not a page boundary — the underlying query
 * has no sort, so which records it saw is arbitrary, and paging within it
 * would present incompleteness as a page. Anything this function cannot
 * positively confirm complete is treated as incomplete.
 *
 * Exported so it can be tested without rendering or a network call.
 */
export function isPreviewInventoryComplete(preview) {
  if (!preview) return false;
  if (preview.truncated === true) return false;
  if (preview.inventory_complete !== true) return false;
  if (preview.stop_reason) return false;
  // `null` counts are the server's explicit "this is not an inventory"
  // signal. They must never be read as zero.
  if (preview.blocker_counts == null) return false;
  if (preview.category_counts == null) return false;
  return true;
}

//: Backend error codes this card is willing to show verbatim. Anything
//: else becomes a generic operator message — a `detail` from the server
//: is arbitrary text (and can be an object, an array, or HTML) and must
//: never be rendered straight into the page.
export const RESIDUE_SAFE_ERROR_CODES = {
  catalog_not_found: "That catalog was not found for this tenant.",
  internal_error: "The server could not complete the preview.",
  company_id_is_required: "A tenant must be resolved before running the preview.",
};

/**
 * Pure: an operator-safe message for a failed request.
 *
 * Preserves the HTTP status, which is genuinely useful, and maps a
 * RECOGNISED backend code to fixed wording. An unrecognised code, a
 * non-string detail, or an object/array/HTML-shaped detail all collapse
 * to the caller's generic fallback. Raw server text is never rendered.
 */
export function sanitizeResidueError(err, fallback = "The request failed.") {
  const status = err?.response?.status;
  const raw = err?.response?.data?.detail;
  const code = typeof raw === "string" ? raw.trim() : "";
  const known = Object.prototype.hasOwnProperty.call(RESIDUE_SAFE_ERROR_CODES, code)
    ? RESIDUE_SAFE_ERROR_CODES[code]
    : "";
  const message = known || fallback;
  return status ? `${message} (HTTP ${status})` : message;
}

//: Largest page a caller may request. Mirrors `ResiduePreviewIn`'s own
//: `le=200` bound, so a page figure this card believes is one the server
//: would also have been willing to serve.
export const RESIDUE_MAX_PAGE_LIMIT = 200;

/**
 * Pure: is this a page count this card is willing to print?
 *
 * A record count is a cardinality, exactly like a blocker count: a whole
 * non-negative number inside the range JavaScript can add without losing
 * precision. `Number.isSafeInteger` rejects `NaN`, both infinities,
 * fractions and unsafe magnitudes in one predicate; the `typeof` check
 * keeps `true`, `"1"` and `null` away from it.
 */
export function isValidPageCount(value) {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

/**
 * Pure: why this page metadata cannot be trusted, or `""` if it can.
 *
 * Final delta review of head `70dbf22`: `typeof page.returned === "number"`
 * is satisfied by `NaN`, and nothing rejected a negative, a fraction, an
 * unsafe integer, or `returned > total`. The card printed "Showing 7 of 1"
 * beside a clean banner, and the Next control took its enablement from
 * arithmetic on those values.
 *
 * Every field is checked, and the fields are checked against EACH OTHER:
 * a page that claims more records than the inventory holds, or an offset
 * past the end of it, is contradictory rather than merely odd. Nothing is
 * coerced, clamped, rounded or defaulted.
 */
export function describeResiduePageProblem(page) {
  if (!page || typeof page !== "object" || Array.isArray(page)) {
    return "The server response is missing its page metadata. "
      + "Treat this as incomplete and escalate.";
  }
  const { returned, total, offset, limit } = page;
  if (!isValidPageCount(returned) || !isValidPageCount(total)) {
    return "The server reported a page count this console cannot read as a whole "
      + "number of records. Treat this as incomplete and escalate.";
  }
  if (!isValidPageCount(offset)) {
    return "The server reported an unreadable page offset. "
      + "Treat this as incomplete and escalate.";
  }
  if (!isValidPageCount(limit) || limit < 1 || limit > RESIDUE_MAX_PAGE_LIMIT) {
    return "The server reported a page size outside the range this endpoint serves. "
      + "Treat this as incomplete and escalate.";
  }
  if (returned > total) {
    return "The server reported more records on this page than exist in the whole "
      + "inventory. Treat this as incomplete and escalate.";
  }
  if (returned > limit) {
    return "The server reported more records than the page size allows. "
      + "Treat this as incomplete and escalate.";
  }
  if (offset > total) {
    return "The server reported a page beyond the end of the inventory. "
      + "Treat this as incomplete and escalate.";
  }
  // The boundary itself must stay inside the safe-integer range, so no
  // navigation arithmetic can silently lose precision.
  if (!Number.isSafeInteger(offset + returned) || offset + returned > total) {
    return "The server's page boundary is inconsistent with the inventory total. "
      + "Treat this as incomplete and escalate.";
  }
  if (!Number.isSafeInteger(offset + limit)) {
    return "The server's page boundary cannot be advanced safely. "
      + "Treat this as incomplete and escalate.";
  }
  return "";
}

/**
 * Pure: why this payload cannot be trusted, or `""` if it can.
 *
 * Fails closed. A null body, a non-object, or a missing required field
 * is an unusable answer, not an empty one — rendering it would let a
 * malformed response read as a clean tenant.
 */
export function describeResiduePayloadProblem(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return "The server returned an unreadable response. Treat this as incomplete and escalate.";
  }
  if (typeof data.truncated !== "boolean" || typeof data.inventory_complete !== "boolean") {
    return "The server response is missing its completeness fields. Treat this as incomplete and escalate.";
  }
  if (data.inventory_complete === true) {
    // A complete inventory cannot also be truncated. Contradiction is not
    // ambiguity — it means the two fields disagree and neither can be
    // trusted.
    if (data.truncated === true) {
      return "The server reported a complete inventory that was also truncated. "
        + "Treat this as incomplete and escalate.";
    }
    if (data.stop_reason) {
      return "The server reported a complete inventory with a stop reason. "
        + "Treat this as incomplete and escalate.";
    }
    const pageProblem = describeResiduePageProblem(data.page);
    if (pageProblem) return pageProblem;
    if (data.blocker_counts == null || typeof data.blocker_counts !== "object") {
      return "The server reported a complete inventory with no blocker counts. "
        + "Treat this as incomplete and escalate.";
    }
  }
  return "";
}

//: The three aggregate outcomes an operator may be shown. `CLEAN` is
//: deliberately worded as "no blockers reported by this preview" — never
//: "safe to activate". A clean preview is necessary for activation and
//: does not authorize it.
export const RESIDUE_OUTCOME_STOP = "stop";
export const RESIDUE_OUTCOME_BLOCKERS = "blockers";
export const RESIDUE_OUTCOME_CLEAN = "clean";

//: The blocker keys `production_activation._check_pre_activation_residue`
//: initialises and counts. At least one must be present for a counts
//: object to be recognised at all — an empty object is not "zero
//: blockers", it is an answer this card cannot read.
export const EXPECTED_BLOCKER_KEYS = Object.freeze([
  "no_in_flight_deliveries",
  "no_reusable_quote",
  "no_pre_activation_order",
  "no_cross_environment_deliveries",
]);

/**
 * Pure: is this a blocker count this card is willing to believe?
 *
 * A blocker count is a cardinality: a whole number of records, never
 * negative, never fractional, never beyond the range JavaScript can add
 * without losing precision. `Number.isSafeInteger` rejects `NaN`, both
 * infinities, fractions and unsafe magnitudes in one predicate;
 * `typeof === "number"` is still needed because `true`, `"3"` and `null`
 * would otherwise reach it, and `Number.isSafeInteger(true)` is false
 * only by accident of coercion rules rather than by intent.
 */
export function isValidBlockerCount(value) {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

/**
 * Pure: the single truthful headline for a preview.
 *
 * Fails closed in one direction only — towards STOP. The delta review of
 * head `b2675af` found the previous version accepted any finite number
 * and summed it, so `{no_reusable_quote: -1}`, a pair of counts
 * cancelling to zero, and `{}` all produced the clean banner. `-Infinity`
 * did too, because `typeof -Infinity === "number"` and
 * `Number.isNaN(-Infinity)` is false.
 *
 * None of those is reachable through this backend, which initialises the
 * four keys above to zero and only increments. That makes this hardening
 * rather than an incident report — but a card that says "no blockers" on
 * a count it could not actually read is failing in precisely the
 * direction that matters, so nothing is coerced, clamped or skipped:
 *
 *   * a non-object, null, or array `blocker_counts` -> STOP
 *   * no expected key present (including `{}`)      -> STOP
 *   * ANY value that is not a non-negative safe integer -> STOP,
 *     including one sitting beside otherwise valid counts
 *
 * Every entry is consumed, expected or not: an unrecognised key with a
 * valid count is ADDED to the total rather than ignored, so a blocker
 * this UI does not yet know about raises the number instead of
 * disappearing from it.
 */
export function summarizeResidueOutcome(preview) {
  const stop = {
    kind: RESIDUE_OUTCOME_STOP, blockerTotal: null, text: "STOP — inventory incomplete",
  };
  if (!isPreviewInventoryComplete(preview)) return stop;
  // Pagination is part of the answer: a page figure this card cannot read
  // makes the whole result unreadable, not merely imprecise.
  if (describeResiduePageProblem(preview.page)) return stop;

  const counts = preview.blocker_counts;
  if (!counts || typeof counts !== "object" || Array.isArray(counts)) return stop;

  const entries = Object.entries(counts);
  if (!entries.length) return stop;
  if (!EXPECTED_BLOCKER_KEYS.some((k) => Object.prototype.hasOwnProperty.call(counts, k))) {
    return stop;
  }
  if (!entries.every(([, v]) => isValidBlockerCount(v))) return stop;

  const blockerTotal = entries.reduce((sum, [, v]) => sum + v, 0);
  if (!Number.isSafeInteger(blockerTotal)) return stop;

  if (blockerTotal > 0) {
    return {
      kind: RESIDUE_OUTCOME_BLOCKERS, blockerTotal,
      text: `Blockers present: ${blockerTotal} — activation remains prohibited`,
    };
  }
  return {
    kind: RESIDUE_OUTCOME_CLEAN, blockerTotal: 0,
    text: "Inventory complete — no blockers reported by this preview",
  };
}

/**
 * Pure: may this preview be paged FORWARD from `offset`?
 *
 * Every input is validated first, so no comparison is ever performed on a
 * value the card could not read. An unreadable page can never enable
 * navigation, and the next boundary must itself stay a safe integer.
 */
export function canPageResidueForward(preview) {
  if (!isPreviewInventoryComplete(preview)) return false;
  if (describeResiduePageProblem(preview?.page)) return false;
  const { returned, total, offset, limit } = preview.page;
  if (!Number.isSafeInteger(offset + limit)) return false;
  return offset + returned < total;
}

/**
 * Pure: may this preview be paged BACKWARD?
 *
 * Both directions read the SERVER's validated `page.offset` rather than a
 * separately tracked component offset. Two sources of truth for the same
 * position is how a page figure and a navigation state come to disagree;
 * there is now only one, and it is the one the validator checked.
 */
export function canPageResidueBackward(preview) {
  if (!isPreviewInventoryComplete(preview)) return false;
  if (describeResiduePageProblem(preview?.page)) return false;
  return preview.page.offset > 0;
}

/**
 * Pure: the offset a navigation click should request, or `null` when the
 * move is not permitted. Never produces an offset outside the bounded
 * inventory, and never one the server's own `le=200` limit would refuse.
 */
export function nextResidueOffset(preview, direction) {
  if (direction === "next") {
    if (!canPageResidueForward(preview)) return null;
    return preview.page.offset + preview.page.limit;
  }
  if (!canPageResidueBackward(preview)) return null;
  return Math.max(0, preview.page.offset - preview.page.limit);
}

/**
 * Pure: may this preview be paged to `offset`?
 *
 * `offset === 0` is a fresh run and always permitted. Any other offset is
 * paging, and paging is permitted ONLY inside an already-complete
 * inventory — a truncated scan has no meaningful page beyond its ceiling,
 * so an offset must never be able to walk past it.
 *
 * Extracted rather than inlined so the SAME rule drives the handler's
 * guard and the Next/Previous disabled state, and so it can be tested
 * without rendering.
 */
export function isResiduePagingPermitted(preview, offset) {
  if (!offset) return true;
  return isPreviewInventoryComplete(preview);
}

/**
 * Pure: reasons the "Run preview" button must stay disabled. Empty array
 * means runnable. Mirrors the other `compute*BlockingReasons` helpers on
 * this page.
 */
export function computeResiduePreviewBlockingReasons({
  isSuperAdmin, isTenantReady, companyId, catalogId, pending,
}) {
  const reasons = [];
  if (!isSuperAdmin) reasons.push("Signed-in role is not super_admin.");
  if (!isTenantReady) reasons.push("Tenant readiness has not passed for this slug.");
  if (!companyId) reasons.push("No server-resolved company for this tenant yet.");
  if (!catalogId) reasons.push("No catalog selected.");
  if (pending) reasons.push("A preview request is already in flight.");
  return reasons;
}

/**
 * Pure: render a count that may legitimately be `null`. `null` means the
 * server refused to report an inventory; showing `0` there would tell an
 * operator the tenant is clean when nothing was actually counted.
 */
export function formatResidueCount(value) {
  return value === null || value === undefined ? "—" : String(value);
}

/**
 * Pure decision function for the tenant-readiness gate — never touches
 * the network. Exported for the same standalone-testability reason as
 * `computeConfirmBlockingReasons` below. `readiness` is the exact
 * response body of `GET .../tenants/by-slug/{slug}/readiness`, or
 * `null`/`undefined` before any readiness check has run.
 *
 * Coordinate-diagnosis remediation (Aug 2026 — UBE-ORD-000010): this is
 * the pre-order gate. It never inspects a specific order — it exists so
 * the console can refuse to let an operator even START looking up an
 * order (and, by extension, instructing a Product Owner to create and
 * pay for one) until the tenant's own configuration is provably capable
 * of producing a complete-coordinates preview.
 */
// ===========================================================================
// Local-residue reconciliation (Sep 2026, Uber go-live blocker
// investigation) — pure helpers.
//
// The activation residue preview above is READ-ONLY and stays that way.
// This second, separate control is the ONE place a super admin can close
// the local-only residue class ("quoted and abandoned, paid, Uber never
// contacted") through the backend's two-step preview + apply. Everything
// that decides eligibility, mints the signed plan, re-validates it and
// writes lives in the backend (`residue_reconciliation.py`); this page
// only shows what the backend classified, requires the typed
// confirmation the plan names, and disables the control in every state
// the backend would refuse anyway. A disabled button here is never the
// safety mechanism — it is a courtesy in front of a backend refusal.
//
// Deliberately named WITHOUT "residue" in any function or state name:
// the AST source guard for the read-only preview card collects every
// residue-named function and forbids it from reaching a mutating
// endpoint, and this control MUST reach one. Keeping the two name spaces
// disjoint is what lets that guard stay exact.
// ===========================================================================

export const LOCAL_RECONCILIATION_PREVIEW_ENDPOINT = "/internal-admin/uber-direct/residue-reconciliation/preview";
export const LOCAL_RECONCILIATION_APPLY_ENDPOINT = "/internal-admin/uber-direct/residue-reconciliation/apply";

//: The sentence this control must always carry, verbatim.
export const LOCAL_RECONCILIATION_SCOPE_SENTENCE =
  "Reconciliation does not authorize production activation or a production order.";

export const LOCAL_RECONCILIATION_CATEGORY_LABELS = {
  eligible_local_reconciliation: "Eligible — local residue, closes on apply",
  non_blocking_historical: "Non-blocking — already terminal and settled",
  operator_action_required: "HAZARD — operator payment action required (excluded)",
  provider_truth_required: "HAZARD — provider truth required (excluded)",
  not_reconcilable: "Not reconcilable — stays as it is",
  unknown_fail_closed: "Unknown — fail closed, stays as it is",
};

export const LOCAL_RECONCILIATION_CATEGORY_ORDER = [
  "eligible_local_reconciliation", "operator_action_required", "provider_truth_required",
  "not_reconcilable", "unknown_fail_closed", "non_blocking_historical",
];

export const LOCAL_RECONCILIATION_HAZARD_CATEGORIES = [
  "operator_action_required", "provider_truth_required",
];

//: Backend codes this control is willing to name. Anything else becomes
//: the generic fallback — a server `detail` is arbitrary text, or an
//: object, and is never rendered.
export const LOCAL_RECONCILIATION_SAFE_ERROR_CODES = {
  environment_mismatch: "The tenant's current provider mode no longer matches this environment.",
  environment_invalid: "The environment is not one this control can reconcile.",
  plan_stale: "A record changed since the preview. Nothing was written. Run the preview again.",
  plan_expired: "The plan expired. Nothing was written. Run the preview again.",
  plan_signature_invalid: "The plan could not be verified. Nothing was written. Run the preview again.",
  plan_malformed: "The plan could not be read. Nothing was written. Run the preview again.",
  plan_company_mismatch: "The plan belongs to a different tenant. Nothing was written.",
  plan_environment_mismatch: "The plan belongs to a different environment. Nothing was written.",
  plan_classifier_version_mismatch: "The server's classifier changed since the preview. Run the preview again.",
  plan_empty: "The plan contains no eligible record.",
  confirmation_mismatch: "The typed confirmation did not match. Nothing was written.",
  plan_signing_unavailable: "The server cannot sign a plan right now.",
  company_id_required: "A tenant must be resolved before running the preview.",
};

/**
 * Pure: an operator-safe message for a failed reconciliation request.
 * Recognised backend codes (the `code` field of an object `detail`, or a
 * bare string `detail`) map to fixed wording; anything else is the
 * generic fallback. The HTTP status is kept. Raw server text is never
 * rendered.
 */
export function sanitizeLocalReconciliationError(err, fallback = "The request failed.") {
  const status = err?.response?.status;
  const raw = err?.response?.data?.detail;
  const code = typeof raw === "string"
    ? raw.trim()
    : (raw && typeof raw === "object" && typeof raw.code === "string") ? raw.code.trim() : "";
  const known = Object.prototype.hasOwnProperty.call(LOCAL_RECONCILIATION_SAFE_ERROR_CODES, code)
    ? LOCAL_RECONCILIATION_SAFE_ERROR_CODES[code]
    : "";
  const message = known || fallback;
  return status ? `${message} (HTTP ${status})` : message;
}

/** Pure: a count this control is willing to print (a real cardinality). */
export function isValidLocalReconciliationCount(value) {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

export function formatLocalReconciliationCount(value) {
  return isValidLocalReconciliationCount(value) ? String(value) : "—";
}

/**
 * Pure: why a preview payload cannot be trusted, or `""` if it can.
 * Checks the fields this control acts on — identity, the read-only
 * marker, the truncation contract (null counts with `truncated`, real
 * counts otherwise), the candidate list and the plan's shape.
 */
export function describeLocalReconciliationPayloadProblem(data, { companyId, environment } = {}) {
  if (!data || typeof data !== "object") return "The server returned no preview.";
  if (data.read_only !== true) return "The server did not mark the preview read-only.";
  if (companyId && data.company_id !== companyId) return "The preview names a different tenant.";
  if (environment && data.environment !== environment) return "The preview names a different environment.";
  if (data.truncated === true) {
    if (data.inventory_complete !== false || data.category_counts != null || data.plan != null) {
      return "The server reported a truncated scan with inconsistent fields.";
    }
    return "";
  }
  if (data.truncated !== false || data.inventory_complete !== true) {
    return "The server did not state whether the scan was complete.";
  }
  const counts = data.category_counts;
  if (!counts || typeof counts !== "object") return "The preview carries no category counts.";
  for (const key of LOCAL_RECONCILIATION_CATEGORY_ORDER) {
    if (!isValidLocalReconciliationCount(counts[key])) return `The count for "${key}" is not a valid number.`;
  }
  if (!Array.isArray(data.candidates)) return "The preview carries no candidate list.";
  if (!isValidLocalReconciliationCount(data.total) || data.total !== data.candidates.length) {
    return "The preview's total does not match its candidate list.";
  }
  const eligible = counts.eligible_local_reconciliation;
  if (eligible > 0) {
    const plan = data.plan;
    if (!plan || typeof plan !== "object") return "Eligible records were reported without a plan.";
    if (typeof plan.plan_token !== "string" || !plan.plan_token) return "The plan carries no token.";
    if (typeof plan.plan_id !== "string" || !plan.plan_id) return "The plan carries no id.";
    if (typeof plan.confirmation_text_required !== "string" || !plan.confirmation_text_required) {
      return "The plan names no confirmation text.";
    }
    if (plan.candidate_count !== eligible) return "The plan's candidate count does not match the eligible count.";
    if (!Number.isFinite(Date.parse(plan.expires_at))) return "The plan carries no readable expiry.";
  } else if (data.plan != null) {
    return "A plan was returned although nothing is eligible.";
  }
  return "";
}

/** Pure: has the plan lapsed as of `nowMs`? A missing/unreadable expiry counts as lapsed. */
export function isLocalReconciliationPlanExpired(plan, nowMs) {
  const at = Date.parse(plan?.expires_at);
  if (!Number.isFinite(at)) return true;
  return at <= nowMs;
}

/**
 * Pure: every reason the apply control must stay disabled, in display
 * order. Empty means the operator may apply. Each reason mirrors a check
 * the backend makes on its own; none of them is the safety mechanism.
 */
export function computeLocalReconciliationBlockingReasons({
  isSuperAdmin, isTenantReady, companyId, environment, preview, nowMs,
  typedConfirmation, typedTenantSlug, expectedTenantSlug, applying, contextChanged,
}) {
  const reasons = [];
  if (!isSuperAdmin) reasons.push("Only a super admin may reconcile.");
  if (!isTenantReady) reasons.push("Resolve a ready tenant first.");
  if (!companyId || !environment) reasons.push("The tenant's company and environment must be known.");
  if (contextChanged) reasons.push("The tenant or environment changed since this preview — run the preview again.");
  if (!preview) {
    reasons.push("Run the reconciliation preview first.");
    return reasons;
  }
  if (preview.truncated === true || preview.inventory_complete !== true) {
    reasons.push("STOP — the scan was truncated; nothing can be applied from an incomplete inventory.");
    return reasons;
  }
  const eligible = preview.category_counts?.eligible_local_reconciliation;
  if (!isValidLocalReconciliationCount(eligible) || eligible === 0) {
    reasons.push("No record is eligible for local reconciliation.");
    return reasons;
  }
  const plan = preview.plan;
  if (!plan) {
    reasons.push("The preview carries no plan.");
    return reasons;
  }
  if (isLocalReconciliationPlanExpired(plan, nowMs)) {
    reasons.push("The plan expired — run the preview again.");
  }
  if ((typedConfirmation || "").trim() !== plan.confirmation_text_required) {
    reasons.push("Type the confirmation text exactly as shown.");
  }
  if (!expectedTenantSlug || (typedTenantSlug || "").trim() !== expectedTenantSlug) {
    reasons.push("Type the tenant slug exactly as resolved.");
  }
  if (applying) reasons.push("An apply is already in flight.");
  return reasons;
}

// ---------------------------------------------------------------------------
// One-time UBERTEST campaign retirement (Sep 2026, Uber go-live)
//
// A NARROW, ONE-TIME go-live mechanism — not a general deletion tool, not
// reconciliation, and not tenant diagnostics.
//
// It RETIRES an approved disposable test campaign. It does NOT resolve
// anything: no payment is captured, cancelled or refunded, no Uber
// delivery is cancelled or modified, no delivery status, order state or
// payment label is changed, and nothing is deleted. Every historical
// payment and provider fact is preserved exactly as it stands, and this
// card says so on every screen an operator sees.
//
// The server resolves the tenant itself from a fixed lowercase slug,
// fixes the environment to sandbox, and restricts the campaign to a
// server-side allowlist of eighteen approved order codes; these requests
// therefore carry no company, no environment and no order code at all.
// Nothing on this card is the safety mechanism — every check here is
// mirrored by one the backend makes on its own.
// ---------------------------------------------------------------------------

export const UBERTEST_RETIREMENT_PREVIEW_ENDPOINT =
  "/internal-admin/uber-direct/ubertest-campaign-retirement/preview";
export const UBERTEST_RETIREMENT_APPLY_ENDPOINT =
  "/internal-admin/uber-direct/ubertest-campaign-retirement/apply";
export const UBERTEST_RETIREMENT_VERIFY_ENDPOINT =
  "/internal-admin/uber-direct/ubertest-campaign-retirement/verify";

//: The ONLY tenant and environment this card may ever act against.
//: Lowercase, exactly as the backend resolves it and exactly as the
//: operator must type it.
export const UBERTEST_RETIREMENT_TENANT_SLUG = "ubertest";
export const UBERTEST_RETIREMENT_ENVIRONMENT = "sandbox";

//: The campaign is exactly eighteen records. Not "at most".
export const UBERTEST_RETIREMENT_CAMPAIGN_SIZE = 18;

//: Sentences this control must always carry, verbatim.
export const UBERTEST_RETIREMENT_SCOPE_SENTENCE =
  "Retirement does not authorize production activation or a production order.";
export const UBERTEST_RETIREMENT_PRESERVATION_SENTENCE =
  "Historical sandbox truth is preserved. Payment and provider states are not "
  + "resolved or changed.";
export const UBERTEST_RETIREMENT_COMPLETENESS_SENTENCE =
  "All 18 approved campaign records must be present.";
export const UBERTEST_RETIREMENT_INDEPENDENT_SENTENCE =
  "The ordinary activation preview must independently confirm zero blockers afterward.";

//: Membership verdicts, in the order the card lists them.
export const UBERTEST_RETIREMENT_MEMBERSHIP_ORDER = [
  "campaign_member_retirement_eligible",
  "already_retired",
  "unknown_campaign_evidence_fail_closed",
  "not_in_approved_campaign",
];

/**
 * Pure: the sanitized reason identifiers for one record, as one line.
 *
 * The API has always returned `reasons` — a list of FIXED identifiers
 * such as `order_placed_at_unreadable` — and this table never rendered
 * it. That is why a live preview that failed 18 of 18 could only say
 * "fails closed" with a blank historical category, while the exact
 * cause sat unread in the response the browser already had.
 *
 * Only the identifiers are shown. They are constants emitted by the
 * classifier, so no order id, customer, address, amount, payment,
 * provider identifier or credential can reach the page through them —
 * anything that is not a plain string is dropped rather than rendered.
 */
export function formatUbertestRetirementReasons(reasons) {
  if (!Array.isArray(reasons)) return "—";
  const safe = reasons.filter((r) => typeof r === "string" && r.trim());
  return safe.length ? safe.join(", ") : "—";
}

export const UBERTEST_RETIREMENT_MEMBERSHIP_LABELS = {
  campaign_member_retirement_eligible: "Approved campaign member — will be retired",
  already_retired: "Already retired",
  unknown_campaign_evidence_fail_closed: "Unknown / mismatched — EXCLUDED, fails closed",
  not_in_approved_campaign: "Outside the approved campaign — EXCLUDED",
};

//: Historical categories, DISPLAY ONLY. None of these ever reads as
//: delivered, cancelled, refunded, settled or provider-completed —
//: because none of that is being asserted.
export const UBERTEST_RETIREMENT_HISTORICAL_LABELS = {
  retired_local_only_sandbox_test_residue: "Local-only sandbox test residue",
  retired_sandbox_test_with_final_payment_evidence:
    "Payment evidence final — retained unchanged",
  retired_sandbox_test_with_movable_payment_evidence_retained:
    "Stripe TEST money can still move — RETAINED, not resolved",
  retired_sandbox_test_with_dispatch_attempt_evidence_retained:
    "Dispatch attempt exists — RETAINED, provider may have been contacted",
  retired_sandbox_test_with_provider_delivery_evidence_retained:
    "Provider delivery id present — RETAINED, not resolved",
  retired_sandbox_test_with_provider_event_evidence_retained:
    "Provider events present — RETAINED, not resolved",
  retired_sandbox_test_legacy_missing_test_marker:
    "Legacy record — no internal_test marker",
};

//: Membership verdicts that must be rendered as EXCLUDED.
export const UBERTEST_RETIREMENT_EXCLUDED_MEMBERSHIPS = [
  "unknown_campaign_evidence_fail_closed",
  "not_in_approved_campaign",
];

export const UBERTEST_RETIREMENT_SAFE_ERROR_CODES = {
  campaign_retirement_disabled:
    "The one-time campaign-retirement control is not enabled in this deployment.",
  transactions_unavailable:
    "This deployment cannot provide an atomic multi-document transaction, so nothing "
    + "was written. Retirement is refused rather than applied in pieces.",
  tenant_unresolved: "The approved campaign tenant could not be resolved.",
  environment_mismatch: "This control runs against the sandbox environment only.",
  plan_expired: "The plan expired. Nothing was written. Run the preview again.",
  plan_stale: "A record changed since the preview. Nothing was written. Preview again.",
  plan_incomplete_campaign:
    "The plan does not carry all 18 approved records. Nothing was written.",
  plan_signature_invalid: "The plan could not be verified. Nothing was written.",
  plan_malformed: "The plan could not be read. Nothing was written.",
  plan_company_mismatch: "That plan belongs to another tenant. Nothing was written.",
  plan_campaign_mismatch: "That plan belongs to another campaign. Nothing was written.",
  plan_candidate_not_approved:
    "The plan names a record outside the approved campaign. Nothing was written.",
  plan_duplicate_candidate: "The plan names a record twice. Nothing was written.",
  campaign_already_completed:
    "This campaign was already retired. Use the verification action.",
  confirmation_mismatch: "The confirmation text did not match exactly.",
  tenant_confirmation_mismatch:
    "The typed tenant slug did not match exactly (it is lowercase).",
  plan_signing_unavailable: "The server cannot sign a plan. Nothing was written.",
};

/**
 * Pure. Turn an axios error into text safe to render. Never renders a
 * raw backend message, a stack, a driver error or an identifier.
 */
export function sanitizeUbertestRetirementError(err, fallback) {
  const detail = err?.response?.data?.detail;
  const code = typeof detail === "object" && detail ? detail.code : undefined;
  const known = Object.prototype.hasOwnProperty.call(
    UBERTEST_RETIREMENT_SAFE_ERROR_CODES, code)
    ? UBERTEST_RETIREMENT_SAFE_ERROR_CODES[code]
    : null;
  return known || fallback || "The request was refused.";
}

/**
 * Pure. Reject a payload this card must not render. Returns a
 * human-readable problem, or "" when the payload is coherent.
 */
export function describeUbertestRetirementPayloadProblem(data, tenantSlug) {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return "The server returned an unreadable response.";
  }
  if (data.tenant_slug !== UBERTEST_RETIREMENT_TENANT_SLUG) {
    return "The server answered for a different tenant.";
  }
  if (tenantSlug && tenantSlug !== UBERTEST_RETIREMENT_TENANT_SLUG) {
    return "This control is bound to the ubertest tenant only.";
  }
  if (data.environment !== UBERTEST_RETIREMENT_ENVIRONMENT) {
    return "The server answered for a different environment.";
  }
  if (data.changes_payment_or_provider_state !== false) {
    return "The server did not confirm that payment and provider state are unchanged.";
  }
  if (data.truncated === true) {
    if (data.inventory_complete !== false || data.campaign_inventory != null
        || data.plan != null) {
      return "The server reported a truncated scan with inconsistent fields.";
    }
    return "";
  }
  if (data.truncated !== false || data.inventory_complete !== true) {
    return "The server did not report a complete inventory.";
  }
  const inv = data.campaign_inventory;
  if (!inv || typeof inv !== "object") {
    return "The server returned no campaign inventory.";
  }
  for (const key of ["approved", "present", "retirement_eligible", "already_retired",
                     "unknown_or_mismatched"]) {
    const value = inv[key];
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
      return "The server returned an unreadable campaign inventory.";
    }
  }
  if (inv.approved !== UBERTEST_RETIREMENT_CAMPAIGN_SIZE) {
    return "The server's approved campaign size is not 18.";
  }
  if (!Array.isArray(data.records)) {
    return "The server returned no record list.";
  }
  const plan = data.plan;
  if (plan != null) {
    if (typeof plan !== "object" || Array.isArray(plan)) return "The plan is unreadable.";
    if (typeof plan.plan_id !== "string" || !plan.plan_id) return "The plan carries no id.";
    if (typeof plan.plan_token !== "string" || !plan.plan_token) {
      return "The plan carries no token.";
    }
    if (typeof plan.confirmation_text_required !== "string"
        || !plan.confirmation_text_required) {
      return "The plan carries no confirmation text.";
    }
    if (plan.candidate_count !== UBERTEST_RETIREMENT_CAMPAIGN_SIZE) {
      return "The plan does not carry all 18 approved records.";
    }
  }
  return "";
}

/**
 * Pure. Is the plan expired as far as this browser can tell?
 */
export function isUbertestRetirementPlanExpired(plan, nowMs) {
  if (!plan || typeof plan.expires_at !== "string") return true;
  const expiry = Date.parse(plan.expires_at);
  if (Number.isNaN(expiry)) return true;
  return expiry <= (typeof nowMs === "number" ? nowMs : Date.now());
}

/**
 * Pure decision function — never touches the network. Returns the list of
 * reasons apply must stay disabled. Empty means apply may proceed.
 *
 * Apply is enabled ONLY for an exact, complete, eighteen-record plan.
 */
export function computeUbertestRetirementApplyBlockingReasons({
  isSuperAdmin, isTenantReady, tenantSlug, environment, preview,
  typedConfirmation, typedTenantSlug, applying, contextChanged, nowMs,
}) {
  const reasons = [];
  if (!isSuperAdmin) reasons.push("Super-admin only.");
  if (!isTenantReady) reasons.push("Resolve the tenant first.");
  if (tenantSlug !== UBERTEST_RETIREMENT_TENANT_SLUG) {
    reasons.push(
      `This one-time campaign retirement is bound to the ${UBERTEST_RETIREMENT_TENANT_SLUG}`
      + " tenant only.");
  }
  if (environment !== UBERTEST_RETIREMENT_ENVIRONMENT) {
    reasons.push(`This one-time campaign retirement runs in ${UBERTEST_RETIREMENT_ENVIRONMENT}`
      + " only.");
  }
  if (contextChanged) {
    reasons.push("The tenant or environment changed since this preview — run it again.");
  }
  if (!preview) reasons.push("Run the read-only preview first.");
  if (preview && preview.truncated === true) {
    reasons.push("STOP — the scan was truncated; nothing can be retired from an "
      + "incomplete inventory.");
  }
  if (preview && preview.campaign_completed === true) {
    reasons.push("This campaign is already retired — use the verification action.");
  }
  const inv = preview && preview.campaign_inventory;
  if (preview && preview.truncated === false) {
    if (!inv) {
      reasons.push("The preview returned no campaign inventory.");
    } else {
      if (inv.present !== UBERTEST_RETIREMENT_CAMPAIGN_SIZE) {
        reasons.push(`All ${UBERTEST_RETIREMENT_CAMPAIGN_SIZE} approved records must be `
          + `present — the preview found ${inv.present}.`);
      }
      if (Array.isArray(inv.missing_approved_codes) && inv.missing_approved_codes.length) {
        reasons.push("Approved records are missing from the live inventory.");
      }
      if (Array.isArray(inv.duplicate_approved_codes)
          && inv.duplicate_approved_codes.length) {
        reasons.push("An approved order code resolves to more than one record.");
      }
      if (Array.isArray(inv.extra_activation_blockers_outside_campaign)
          && inv.extra_activation_blockers_outside_campaign.length) {
        reasons.push("An activation blocker exists outside the approved campaign.");
      }
      if (inv.unknown_or_mismatched > 0) {
        reasons.push("Unknown or mismatched campaign evidence — fails closed.");
      }
      if (inv.retirement_eligible !== UBERTEST_RETIREMENT_CAMPAIGN_SIZE) {
        reasons.push("Not every approved record is retirement-eligible.");
      }
    }
  }
  const plan = preview && preview.plan;
  if (preview && !plan) reasons.push("The preview minted no plan.");
  if (plan && plan.candidate_count !== UBERTEST_RETIREMENT_CAMPAIGN_SIZE) {
    reasons.push("The plan does not carry all 18 approved records.");
  }
  if (plan && isUbertestRetirementPlanExpired(plan, nowMs)) {
    reasons.push("The plan expired — run the preview again.");
  }
  if (plan && (typedConfirmation || "").trim() !== plan.confirmation_text_required) {
    reasons.push("Type the confirmation text exactly as shown.");
  }
  if ((typedTenantSlug || "").trim() !== UBERTEST_RETIREMENT_TENANT_SLUG) {
    reasons.push(
      `Type the tenant slug exactly as resolved: ${UBERTEST_RETIREMENT_TENANT_SLUG}`);
  }
  if (applying) reasons.push("A campaign retirement is already in flight.");
  return reasons;
}

export function computeTenantReadinessBlockingReasons(readiness) {
  if (!readiness) {
    return ["Tenant readiness has not been checked yet."];
  }
  if (!readiness.ready) {
    return (readiness.blocking_reasons && readiness.blocking_reasons.length > 0)
      ? readiness.blocking_reasons
      : ["Tenant is not ready for a controlled TEST dispatch attempt."];
  }
  return [];
}

/**
 * Pure decision function — never touches the network, never reads React
 * state directly. Exported so it can be unit-tested in isolation (this
 * repo's frontend test convention has no React Testing Library; the
 * established pattern is to test the exact production predicate as a
 * standalone function — see
 * `frontend/src/pages/__tests__/internal_admin_kitchen_monitoring_display.test.js`).
 *
 * Returns an array of human-readable blocking reasons. Empty array means
 * confirmation may proceed. Every reason listed here maps 1:1 to one of
 * the console's required disable conditions.
 *
 * `readiness` (optional, Aug 2026 coordinate-diagnosis remediation) — the
 * same tenant-readiness response `computeTenantReadinessBlockingReasons`
 * consumes. Omitting it entirely preserves this function's exact prior
 * behavior (existing callers/tests that don't pass it are unaffected);
 * passing it folds any tenant-readiness failure into this same blocking-
 * reasons list, since confirmation must be just as unreachable as order
 * lookup while the tenant isn't ready — the backend's own preview/confirm
 * enforcement (`_require_complete_coordinates_or_raise`) is the real
 * safety boundary either way, this is display/disable logic only.
 */
export function computeConfirmBlockingReasons({
  role, lookup, preview, nowMs, safeRemainingMs = SAFE_REMAINING_MS, readiness,
}) {
  const reasons = [];

  if (role !== "super_admin") {
    reasons.push("Signed-in role is not super_admin.");
  }
  if (readiness !== undefined) {
    reasons.push(...computeTenantReadinessBlockingReasons(readiness));
  }
  if (!lookup || !lookup.eligibility) {
    reasons.push("No eligible order has been selected yet.");
    return reasons; // nothing else is meaningful without a resolved order
  }
  if (!lookup.eligibility.eligible) {
    reasons.push(lookup.eligibility.message || "This order is not eligible for dispatch.");
  }
  const existing = lookup.existing_attempts || {};
  if (existing.has_provider_delivery_id) {
    reasons.push("A prior provider delivery already exists for this delivery.");
  }
  if (existing.in_flight) {
    reasons.push("An ambiguous or in-flight dispatch attempt already exists for this delivery.");
  }
  if (lookup.local_delivery_status === "reconciliation_required" || lookup.local_delivery_status === "delivery_failed") {
    reasons.push(`local_delivery.status is "${lookup.local_delivery_status}" — a terminal operator stop state.`);
  }

  if (!preview) {
    reasons.push("Dispatch preview has not been run yet.");
    return reasons;
  }
  if (!preview.pickup_contact_present || !preview.dropoff_contact_present) {
    reasons.push("Pickup or dropoff contact information is incomplete.");
  }
  const coords = preview.coordinates_present || {};
  if (!coords.pickup || !coords.dropoff) {
    reasons.push("Pickup or dropoff coordinates are incomplete.");
  }
  if (preview.environment && preview.environment !== "sandbox") {
    reasons.push(`Environment is "${preview.environment}", not sandbox.`);
  }
  const quoteExp = preview.quote_expires_at ? new Date(preview.quote_expires_at).getTime() : NaN;
  const authExp = preview.authorization_expires_at ? new Date(preview.authorization_expires_at).getTime() : NaN;
  if (!(quoteExp - nowMs > safeRemainingMs)) {
    reasons.push("Insufficient time remains before the quote expires.");
  }
  if (!(authExp - nowMs > safeRemainingMs)) {
    reasons.push("Insufficient time remains before the authorization expires.");
  }

  return reasons;
}

/**
 * Pure decision function — same testability contract as
 * `computeConfirmBlockingReasons`/`computeTenantReadinessBlockingReasons`
 * above: never touches the network, never reads React state directly.
 *
 * Uber TEST prepayment-marking correction (Aug 2026): gates the
 * "Mark as Internal Test" control shown for a freshly-looked-up,
 * pre-quote order (`markLookup`, the response of
 * `GET .../orders/by-code/{order_code}/for-marking`). Deliberately
 * SEPARATE from `computeConfirmBlockingReasons` — that function gates
 * Preview/Confirm for an order that has already been quoted; this one
 * gates a step that must happen BEFORE any quote exists, so it can
 * never depend on `preview`, `quote_expires_at`, or any
 * quote/delivery-derived field. The backend
 * (`retail/orders.py::mark_internal_test`) independently re-enforces
 * every one of these conditions server-side — this is display/disable
 * logic only, never the real safety boundary.
 */
export function computeMarkBlockingReasons({ role, markLookup }) {
  const reasons = [];
  if (role !== "super_admin") {
    reasons.push("Signed-in role is not super_admin.");
  }
  if (!markLookup) {
    reasons.push("No order has been looked up for marking yet.");
    return reasons;
  }
  if (markLookup.internal_test) {
    reasons.push("This order is already marked as an internal test order.");
    return reasons;
  }
  if (markLookup.payment_status !== "pending") {
    reasons.push(
      `This order's payment_status is "${markLookup.payment_status}" — only a pending order can be ` +
      "marked. A paid, failed, refunded, or cancelled order can never be marked retroactively.",
    );
  }
  return reasons;
}

/**
 * Pure decision function — same testability contract as the other
 * `compute*BlockingReasons` functions above: never touches the
 * network, never reads React state directly.
 *
 * Reconciliation-refund operator control (Aug 2026, UBE-ORD-000017
 * incident-recovery follow-up). `reconLookup` is the exact response
 * body of `GET .../reconciliation-refund/eligibility` — its own
 * `blockers[]` array (one entry per failed backend gate) is surfaced
 * verbatim rather than re-derived, so this function can never drift
 * from the actual, authoritative eligibility logic the backend already
 * computed. `environment_mode` is checked defensively even though the
 * backend itself already blocks a non-`"test"` environment via its own
 * `stripe_not_test_mode` blocker — belt-and-suspenders display logic
 * only, never the real safety boundary.
 */
export function computeReconciliationRefundBlockingReasons({ role, reconLookup }) {
  const reasons = [];
  if (role !== "super_admin") {
    reasons.push("Signed-in role is not super_admin.");
  }
  if (!reconLookup) {
    reasons.push("No order has been looked up for reconciliation refund yet.");
    return reasons;
  }
  if (reconLookup.environment_mode !== "test") {
    reasons.push("Stripe is not configured in TEST/sandbox mode on this environment.");
  }
  if (!reconLookup.eligible) {
    const blockers = reconLookup.blockers || [];
    if (blockers.length > 0) {
      reasons.push(...blockers.map((b) => b.message));
    } else {
      reasons.push("This order is not eligible for a reconciliation refund.");
    }
  }
  return reasons;
}

function msRemaining(iso, nowMs) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return t - nowMs;
}

function fmtRemaining(ms) {
  if (ms == null) return "—";
  if (ms <= 0) return "expired";
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

function PresenceBadge({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border ${
      ok ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-rose-50 border-rose-200 text-rose-700"
    }`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {label}
    </span>
  );
}

/**
 * Eligibility-display correction (Aug 2026). `dispatch_authorization_
 * service.py::get_eligibility_summary` fails FAST on the first blocking
 * precondition (e.g. `not_internal_test_order`) and returns ONLY
 * `{eligible, code, message}` in that case — `pickup_contact_present`,
 * `dropoff_contact_present`, and `coordinates_present` are never even
 * present in the response body, not `false`. Plain `PresenceBadge`
 * (`ok={!!value}`) collapses `undefined` and `false` into the same red
 * "failed" badge, which is misleading: it reads as proof those fields
 * are missing on the order when the truth is the backend never
 * evaluated them at all. This wrapper adds the missing third state.
 * `value === true|false` (the field WAS evaluated) still renders via
 * the exact same `PresenceBadge` as before — this changes rendering
 * only for the previously-collapsed `undefined` case.
 */
function EligibilityPresenceBadge({ value, label }) {
  if (value === undefined || value === null) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border bg-slate-50 border-slate-200 text-slate-500">
        <HelpCircle className="w-3 h-3" /> {label}: Not checked
      </span>
    );
  }
  return <PresenceBadge ok={!!value} label={label} />;
}

/**
 * Super-admin-only controlled Uber Direct TEST dispatch console.
 *
 * Drives the EXISTING backend controlled-TEST dispatch workflow
 * (credential attestation -> dispatch-authorization preview -> confirm ->
 * authoritative delivery detail -> gated poll) end-to-end from a single
 * browser page, replacing the previous Postman-based operator procedure
 * (retired because the Postman web environment repeatedly failed to
 * persist one-time authorization values between requests).
 *
 * The one-time authorization token returned by the preview call NEVER
 * leaves React component state — it is not rendered, not logged, not
 * copyable, and not persisted to localStorage/sessionStorage. It is held
 * only long enough to be attached automatically to the confirm request,
 * then discarded (cleared from state) immediately after that request
 * settles, success or failure.
 *
 * Enforcement of every safety rule (role, sandbox mode, expiry,
 * idempotency, credential attestation, exactly-once dispatch) lives
 * entirely in the existing backend routes this page calls — this
 * component only decides what to SHOW and when to DISABLE the confirm
 * button; it never bypasses a backend check, and a disabled button here
 * is always backed by the same backend check independently refusing the
 * same request.
 */
export default function InternalUberTestDispatchConsole() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  const [tenantSlug, setTenantSlug] = useState("");
  const [readiness, setReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [readinessError, setReadinessError] = useState("");
  // Independent-review correction (Aug 2026): request-generation guard
  // against an out-of-order network response. Without this, checking
  // readiness for tenant A and then quickly re-submitting for tenant B
  // could let a slow tenant-A response land AFTER tenant B's and
  // silently overwrite the displayed readiness with the wrong tenant's
  // result — exactly the class of bug this gate exists to prevent
  // (showing "ready" for a tenant other than the one about to be
  // worked on). Every response checks it's still the LATEST request
  // before touching state; a superseded response is discarded.
  const readinessRequestIdRef = useRef(0);

  // -------- Mark as Internal Test (Aug 2026 prepayment-marking correction) --------
  // Deliberately separate state from the `orderCode`/`lookup` group below —
  // this section resolves and marks a PRE-quote order (no local_delivery
  // record required or expected), while `lookup` below is strictly for an
  // ALREADY-quoted order moving through Preview/Confirm. The two never
  // share state so a successful mark can never accidentally cascade into
  // Preview/Confirm/quote/payment.
  const [markOrderCode, setMarkOrderCode] = useState("");
  const [markLookup, setMarkLookup] = useState(null);
  const [markLookupLoading, setMarkLookupLoading] = useState(false);
  const [markLookupError, setMarkLookupError] = useState("");

  const [markDialogOpen, setMarkDialogOpen] = useState(false);
  const [markTypedOrderCode, setMarkTypedOrderCode] = useState("");
  const [markAckChecked, setMarkAckChecked] = useState(false);
  const [markSubmitting, setMarkSubmitting] = useState(false);
  // "" | "ambiguous" | "confirmed_via_refresh" | "unresolved" — tracks an
  // ambiguous (no-HTTP-response) outcome so the UI can say exactly what is
  // and isn't known, and can refuse further action until it's resolved.
  const [markOutcome, setMarkOutcome] = useState("");

  // -------- Reconciliation-refund operator control (Aug 2026, UBE-ORD-000017 incident-recovery follow-up) --------
  // Deliberately separate state from every group above/below — this
  // section resolves and refunds a STRANDED `reconciliation_required`
  // order (pre-hotfix ownership model), never an already-quoted order
  // moving through the Preview/Confirm dispatch flow above. The two
  // never share state; a reconciliation refund can never accidentally
  // touch dispatch, and vice versa.
  const [reconOrderCode, setReconOrderCode] = useState("");
  const [reconLookup, setReconLookup] = useState(null);
  const [reconLookupLoading, setReconLookupLoading] = useState(false);
  const [reconLookupError, setReconLookupError] = useState("");

  const [reconDialogOpen, setReconDialogOpen] = useState(false);
  const [reconTypedOrderCode, setReconTypedOrderCode] = useState("");
  const [reconAckChecked, setReconAckChecked] = useState(false);
  const [reconSubmitting, setReconSubmitting] = useState(false);
  const [reconResult, setReconResult] = useState(null);
  // "" | "ambiguous" | "unresolved" — network-level ambiguity (no HTTP
  // response at all). Distinct from `reconResult.outcome ===
  // "ambiguous_reconciliation_required"`, which is an APPLICATION-level
  // ambiguous Stripe outcome the backend itself reported inside an
  // ordinary 200 response — both cases forbid blind resubmission, but
  // are tracked separately since they come from different signals.
  const [reconOutcome, setReconOutcome] = useState("");

  const [orderCode, setOrderCode] = useState("");
  const [lookup, setLookup] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");

  const [attestation, setAttestation] = useState(null);
  const [attestationLoading, setAttestationLoading] = useState(false);

  const [preview, setPreview] = useState(null); // holds authorization_id + token, in memory only
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmTypedOrderCode, setConfirmTypedOrderCode] = useState("");
  const [confirmAckChecked, setConfirmAckChecked] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [pollResult, setPollResult] = useState(null);
  const [pollLoading, setPollLoading] = useState(false);

  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const resetForNewOrder = () => {
    setLookup(null); setLookupError("");
    setAttestation(null);
    setPreview(null); setPreviewError("");
    setConfirmResult(null);
    setDetail(null);
    setPollResult(null);
  };

  const resetMarkSection = () => {
    setMarkOrderCode("");
    setMarkLookup(null); setMarkLookupError("");
    setMarkDialogOpen(false);
    setMarkTypedOrderCode(""); setMarkAckChecked(false);
    setMarkOutcome("");
  };

  const runReadinessCheck = async (e) => {
    e?.preventDefault();
    const slug = tenantSlug.trim();
    if (!slug) return;
    const requestId = ++readinessRequestIdRef.current;
    setReadiness(null); setReadinessError("");
    setOrderCode("");
    resetForNewOrder();
    resetMarkSection();
    resetResidueSection();
    resetLocalReconciliationSection();
    setReadinessLoading(true);
    try {
      const { data } = await api.get(
        `/internal-admin/local-delivery/tenants/by-slug/${encodeURIComponent(slug)}/readiness`,
      );
      if (requestId !== readinessRequestIdRef.current) return; // superseded by a newer check
      setReadiness(data);
    } catch (err) {
      if (requestId !== readinessRequestIdRef.current) return; // superseded by a newer check
      setReadinessError(formatApiError(err));
      toast.error(formatApiError(err, "Tenant readiness check failed"));
    } finally {
      if (requestId === readinessRequestIdRef.current) {
        setReadinessLoading(false);
      }
    }
  };

  // Independent-review correction (Aug 2026): `isTenantReady` must be
  // computed BEFORE the "Mark as Internal Test" block below, which
  // references it in a `const` evaluated unconditionally during every
  // render (`canOpenMarkDialog`) — not just inside a later-invoked
  // event handler closure, where declaration order wouldn't matter.
  // Originally declared further down (just above `runLookup`), this
  // threw `ReferenceError: Cannot access 'isTenantReady' before
  // initialization` on every single render of this page, for every
  // user — a temporal-dead-zone bug a rendered test caught immediately
  // that no source-regex test could ever have detected. Purely a
  // relocation: no logic changed.
  const readinessBlockingReasons = useMemo(
    () => computeTenantReadinessBlockingReasons(readiness),
    [readiness],
  );
  const isTenantReady = readinessBlockingReasons.length === 0;

  // ------------- Activation residue preview (read-only) -------------
  //
  // Independent-review correction F1/F2/F3 (Sep 2026). The first version
  // used ONE numeric request-generation ref shared by the catalog load
  // and the preview. A reviewer reproduced tenant A's completed preview —
  // its "Inventory complete" badge and its order codes — rendering under
  // tenant B's name, company and environment badges, because a single
  // counter cannot express "this response belongs to a DIFFERENT tenant",
  // only "this response is older".
  //
  // Responses are now bound to a CONTEXT KEY, not a counter. Every async
  // result must match the identity it was requested under, field by
  // field, or it is discarded whole — including its loading state, so a
  // superseded request's `finally` can never clear a live one's spinner.
  //
  //   identity context  = { gen, slug, companyId }            -> catalogs
  //   preview context   = { gen, slug, companyId, catalogId } -> preview
  //
  // Two contexts rather than one because selecting a catalog must
  // invalidate a pending PREVIEW without discarding the catalog list that
  // is still perfectly valid for the same tenant.
  const residueCompanyId = readiness?.company_id || "";
  const residueEnvironment = readiness?.environment || "";
  const residueTenantSlug = readiness?.tenant_slug || "";
  const residueTenantName = readiness?.tenant_name || residueTenantSlug;

  const [residueCatalogs, setResidueCatalogs] = useState(null);
  const [residueCatalogsLoading, setResidueCatalogsLoading] = useState(false);
  const [residueCatalogsError, setResidueCatalogsError] = useState("");
  const [residueCatalogId, setResidueCatalogId] = useState("");
  const [residuePreview, setResiduePreview] = useState(null);
  const [residuePreviewLoading, setResiduePreviewLoading] = useState(false);
  const [residuePreviewError, setResiduePreviewError] = useState("");
  const [residueOffset, setResidueOffset] = useState(0);

  const residueIdentityRef = useRef({ gen: 0, slug: "", companyId: "" });
  const residuePreviewCtxRef = useRef({ gen: 0, slug: "", companyId: "", catalogId: "" });

  const sameIdentity = (ctx) => {
    const cur = residueIdentityRef.current;
    return ctx.gen === cur.gen && ctx.slug === cur.slug && ctx.companyId === cur.companyId;
  };
  const samePreviewCtx = (ctx) => {
    const cur = residuePreviewCtxRef.current;
    return ctx.gen === cur.gen && ctx.slug === cur.slug
      && ctx.companyId === cur.companyId && ctx.catalogId === cur.catalogId;
  };

  // Everything a preview result occupies, cleared together. Loading is in
  // here deliberately: an identity change must never leave the Run button
  // stuck disabled behind a request whose answer will be thrown away.
  const clearResiduePreviewState = () => {
    setResiduePreview(null);
    setResiduePreviewError("");
    setResidueOffset(0);
    setResiduePreviewLoading(false);
  };

  const resetResidueSection = () => {
    residueIdentityRef.current = {
      gen: residueIdentityRef.current.gen + 1, slug: "", companyId: "",
    };
    residuePreviewCtxRef.current = {
      gen: residuePreviewCtxRef.current.gen + 1, slug: "", companyId: "", catalogId: "",
    };
    setResidueCatalogs(null);
    setResidueCatalogsError("");
    setResidueCatalogsLoading(false);
    setResidueCatalogId("");
    clearResiduePreviewState();
  };

  // Advancing the tenant identity invalidates BOTH contexts and clears
  // every piece of displayed state, then loads that tenant's catalogs.
  useEffect(() => {
    residueIdentityRef.current = {
      gen: residueIdentityRef.current.gen + 1,
      slug: residueTenantSlug,
      companyId: residueCompanyId,
    };
    residuePreviewCtxRef.current = {
      gen: residuePreviewCtxRef.current.gen + 1,
      slug: residueTenantSlug,
      companyId: residueCompanyId,
      catalogId: "",
    };
    setResidueCatalogs(null);
    setResidueCatalogsError("");
    setResidueCatalogId("");
    clearResiduePreviewState();

    if (!isSuperAdmin || !residueCompanyId) {
      setResidueCatalogsLoading(false);
      return undefined;
    }

    const ctx = { ...residueIdentityRef.current };
    let cancelled = false;
    setResidueCatalogsLoading(true);
    api
      .post("/internal-admin/uber-direct/catalog-selector", { company_id: ctx.companyId })
      .then(({ data }) => {
        if (cancelled || !sameIdentity(ctx)) return;
        setResidueCatalogs(data);
        const list = (data && Array.isArray(data.catalogs)) ? data.catalogs : [];
        if (list.length === 1) selectResidueCatalog(list[0].id);
      })
      .catch((err) => {
        if (cancelled || !sameIdentity(ctx)) return;
        setResidueCatalogs(null);
        setResidueCatalogsError(sanitizeResidueError(err, "Could not load catalogs for this tenant"));
      })
      .finally(() => {
        // A superseded response must not touch the live request's state.
        if (cancelled || !sameIdentity(ctx)) return;
        setResidueCatalogsLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, residueCompanyId, residueTenantSlug]);

  // Selecting a catalog invalidates any pending preview and clears the
  // displayed one — a result obtained under a superseded selection is
  // never shown, and returning to the original catalog requires a new,
  // deliberate request.
  function selectResidueCatalog(nextCatalogId) {
    residuePreviewCtxRef.current = {
      gen: residuePreviewCtxRef.current.gen + 1,
      slug: residueTenantSlug,
      companyId: residueCompanyId,
      catalogId: nextCatalogId || "",
    };
    setResidueCatalogId(nextCatalogId || "");
    clearResiduePreviewState();
  }

  const residuePreviewBlockingReasons = useMemo(
    () => computeResiduePreviewBlockingReasons({
      isSuperAdmin,
      isTenantReady,
      companyId: residueCompanyId,
      catalogId: residueCatalogId,
      pending: residuePreviewLoading,
    }),
    [isSuperAdmin, isTenantReady, residueCompanyId, residueCatalogId, residuePreviewLoading],
  );
  const canRunResiduePreview = residuePreviewBlockingReasons.length === 0;
  const residueInventoryComplete = isPreviewInventoryComplete(residuePreview);
  const residueOutcome = summarizeResidueOutcome(residuePreview);

  // ------------- Local-residue reconciliation (preview + apply) -------------
  //
  // Separate state from every group above. Reads the tenant identity
  // straight from `readiness` (never from the residue preview's state),
  // binds every response to the identity it was requested under, and
  // clears itself whenever the tenant changes. `lrPreview.plan.plan_token`
  // is held in memory only, sent back verbatim on apply, and never
  // rendered anywhere.
  const [lrPreview, setLrPreview] = useState(null);
  const [lrLoading, setLrLoading] = useState(false);
  const [lrError, setLrError] = useState("");
  const [lrTyped, setLrTyped] = useState("");
  const [lrTypedSlug, setLrTypedSlug] = useState("");
  const [lrApplying, setLrApplying] = useState(false);
  const [lrResult, setLrResult] = useState(null);
  // "" | "ambiguous" — the apply request got no HTTP response at all.
  // The backend is idempotent, but the operator must re-run the preview
  // (which reports what is now closed) rather than resubmit blind.
  const [lrOutcome, setLrOutcome] = useState("");
  const lrCtxRef = useRef({ gen: 0, slug: "", companyId: "", environment: "" });

  // ---- one-time UBERTEST campaign retirement -------------------------
  const [ucPreview, setUcPreview] = useState(null);
  const [ucLoading, setUcLoading] = useState(false);
  const [ucError, setUcError] = useState("");
  const [ucTyped, setUcTyped] = useState("");
  const [ucTypedSlug, setUcTypedSlug] = useState("");
  const [ucApplying, setUcApplying] = useState(false);
  const [ucResult, setUcResult] = useState(null);
  const [ucVerify, setUcVerify] = useState(null);
  const [ucVerifying, setUcVerifying] = useState(false);
  // "" | "ambiguous" — the apply request got no HTTP response at all.
  // The retirement is one transaction, so it either committed or it did
  // not; the operator finds out which by running the READ-ONLY
  // verification, never by resubmitting.
  const [ucOutcome, setUcOutcome] = useState("");
  const ucCtxRef = useRef({ gen: 0, slug: "", environment: "" });

  const lrCompanyId = readiness?.company_id || "";
  const lrEnvironment = readiness?.environment || "";
  const lrTenantSlug = readiness?.tenant_slug || "";

  const sameLrCtx = (ctx) => {
    const cur = lrCtxRef.current;
    return cur.gen === ctx.gen && cur.slug === ctx.slug
      && cur.companyId === ctx.companyId && cur.environment === ctx.environment;
  };

  const clearLocalReconciliationState = () => {
    setLrPreview(null); setLrError(""); setLrLoading(false);
    setLrTyped(""); setLrTypedSlug("");
    setLrApplying(false); setLrResult(null); setLrOutcome("");
  };

  const resetLocalReconciliationSection = () => {
    lrCtxRef.current = {
      gen: lrCtxRef.current.gen + 1, slug: "", companyId: "", environment: "",
    };
    clearLocalReconciliationState();
  };

  // A new tenant identity invalidates any pending request and clears
  // everything displayed — a plan minted for tenant A can never be
  // applied while tenant B is on screen.
  useEffect(() => {
    lrCtxRef.current = {
      gen: lrCtxRef.current.gen + 1, slug: lrTenantSlug, companyId: lrCompanyId, environment: lrEnvironment,
    };
    clearLocalReconciliationState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lrCompanyId, lrTenantSlug, lrEnvironment]);

  const runLocalReconciliationPreview = async () => {
    if (lrLoading || lrApplying) return;
    if (!isSuperAdmin || !isTenantReady || !lrCompanyId || !lrEnvironment) return;
    lrCtxRef.current = {
      gen: lrCtxRef.current.gen + 1, slug: lrTenantSlug, companyId: lrCompanyId, environment: lrEnvironment,
    };
    const ctx = { ...lrCtxRef.current };
    setLrPreview(null); setLrError(""); setLrResult(null); setLrOutcome("");
    setLrTyped(""); setLrTypedSlug("");
    setLrLoading(true);
    try {
      const { data } = await api.post(LOCAL_RECONCILIATION_PREVIEW_ENDPOINT, {
        company_id: ctx.companyId,
        environment: ctx.environment,
      });
      if (!sameLrCtx(ctx)) return;                    // superseded — discard whole
      const problem = describeLocalReconciliationPayloadProblem(
        data, { companyId: ctx.companyId, environment: ctx.environment });
      if (problem) {
        setLrPreview(null);
        setLrError(problem);
        return;
      }
      setLrPreview(data);
    } catch (err) {
      if (!sameLrCtx(ctx)) return;
      setLrPreview(null);
      setLrError(sanitizeLocalReconciliationError(err, "Reconciliation preview failed"));
      toast.error(sanitizeLocalReconciliationError(err, "Reconciliation preview failed"));
    } finally {
      if (sameLrCtx(ctx)) setLrLoading(false);
    }
  };

  const lrContextChanged = !!lrPreview && (
    lrCtxRef.current.slug !== lrTenantSlug
    || lrCtxRef.current.companyId !== lrCompanyId
    || lrCtxRef.current.environment !== lrEnvironment
  );

  const lrBlockingReasons = useMemo(
    () => computeLocalReconciliationBlockingReasons({
      isSuperAdmin,
      isTenantReady,
      companyId: lrCompanyId,
      environment: lrEnvironment,
      preview: lrPreview,
      nowMs,
      typedConfirmation: lrTyped,
      typedTenantSlug: lrTypedSlug,
      expectedTenantSlug: lrTenantSlug,
      applying: lrApplying,
      contextChanged: lrContextChanged,
    }),
    [isSuperAdmin, isTenantReady, lrCompanyId, lrEnvironment, lrPreview, nowMs,
      lrTyped, lrTypedSlug, lrTenantSlug, lrApplying, lrContextChanged],
  );
  const canApplyLocalReconciliation = lrBlockingReasons.length === 0 && !lrResult && lrOutcome === "";

  const applyLocalReconciliation = async () => {
    if (!canApplyLocalReconciliation || !lrPreview?.plan) return;
    const ctx = { ...lrCtxRef.current };
    const plan = lrPreview.plan;
    setLrApplying(true);
    setLrError("");
    try {
      const { data } = await api.post(LOCAL_RECONCILIATION_APPLY_ENDPOINT, {
        company_id: ctx.companyId,
        environment: ctx.environment,
        plan_token: plan.plan_token,
        confirmation_text: lrTyped.trim(),
      });
      if (!sameLrCtx(ctx)) return;
      setLrResult(data && typeof data === "object" ? data : { outcome: "unreadable" });
      // The plan is spent whatever the outcome; a further apply needs a
      // fresh preview, which is also what reports the new state.
      setLrTyped(""); setLrTypedSlug("");
    } catch (err) {
      if (!sameLrCtx(ctx)) return;
      if (err?.response) {
        setLrError(sanitizeLocalReconciliationError(err, "Reconciliation apply was refused"));
        toast.error(sanitizeLocalReconciliationError(err, "Reconciliation apply was refused"));
        // Any refusal spends the plan on this page: the backend either
        // wrote nothing (plan_stale/expired/mismatch) or the plan is no
        // longer the thing to reuse. Re-run the preview.
        setLrPreview(null);
      } else {
        setLrOutcome("ambiguous");
      }
    } finally {
      if (sameLrCtx(ctx)) setLrApplying(false);
    }
  };

  const sameUcCtx = (ctx) => {
    const cur = ucCtxRef.current;
    return cur.gen === ctx.gen && cur.slug === ctx.slug && cur.environment === ctx.environment;
  };

  const clearUbertestRetirementState = () => {
    setUcPreview(null); setUcError(""); setUcLoading(false);
    setUcTyped(""); setUcTypedSlug("");
    setUcApplying(false); setUcResult(null); setUcOutcome("");
    setUcVerify(null); setUcVerifying(false);
  };

  const ucTenantSlug = readiness?.tenant_slug || "";
  const ucEnvironment = readiness?.environment || "";
  const ucIsApprovedTenant = ucTenantSlug === UBERTEST_RETIREMENT_TENANT_SLUG
    && ucEnvironment === UBERTEST_RETIREMENT_ENVIRONMENT;

  // Any identity change invalidates everything displayed — a plan minted
  // for one tenant can never be applied while another is on screen.
  useEffect(() => {
    ucCtxRef.current = {
      gen: ucCtxRef.current.gen + 1, slug: ucTenantSlug, environment: ucEnvironment,
    };
    clearUbertestRetirementState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ucTenantSlug, ucEnvironment]);

  const runUbertestRetirementPreview = async () => {
    if (ucLoading || ucApplying) return;
    if (!isSuperAdmin || !isTenantReady || !ucIsApprovedTenant) return;
    ucCtxRef.current = {
      gen: ucCtxRef.current.gen + 1, slug: ucTenantSlug, environment: ucEnvironment,
    };
    const ctx = { ...ucCtxRef.current };
    setUcLoading(true);
    setUcError("");
    setUcPreview(null); setUcResult(null); setUcOutcome(""); setUcVerify(null);
    setUcTyped(""); setUcTypedSlug("");
    try {
      const { data } = await api.post(UBERTEST_RETIREMENT_PREVIEW_ENDPOINT, {});
      if (!sameUcCtx(ctx)) return;
      const problem = describeUbertestRetirementPayloadProblem(data, ucTenantSlug);
      if (problem) {
        setUcError(problem);
        toast.error(problem);
        return;
      }
      setUcPreview(data);
    } catch (err) {
      if (!sameUcCtx(ctx)) return;
      const message = sanitizeUbertestRetirementError(
        err, "The campaign-retirement preview was refused");
      setUcError(message);
      toast.error(message);
    } finally {
      if (sameUcCtx(ctx)) setUcLoading(false);
    }
  };

  const ucContextChanged = !!ucPreview && (
    ucCtxRef.current.slug !== ucTenantSlug || ucCtxRef.current.environment !== ucEnvironment
  );

  const ucBlockingReasons = useMemo(
    () => computeUbertestRetirementApplyBlockingReasons({
      isSuperAdmin,
      isTenantReady,
      tenantSlug: ucTenantSlug,
      environment: ucEnvironment,
      preview: ucPreview,
      typedConfirmation: ucTyped,
      typedTenantSlug: ucTypedSlug,
      applying: ucApplying,
      contextChanged: ucContextChanged,
    }),
    [isSuperAdmin, isTenantReady, ucPreview, ucTyped, ucTypedSlug, ucTenantSlug,
     ucEnvironment, ucApplying, ucContextChanged],
  );

  const canApplyUbertestRetirement =
    ucBlockingReasons.length === 0 && !ucResult && ucOutcome === "";

  const applyUbertestRetirement = async () => {
    if (!canApplyUbertestRetirement || !ucPreview?.plan) return;
    const ctx = { ...ucCtxRef.current };
    const plan = ucPreview.plan;
    setUcApplying(true);
    setUcError("");
    try {
      const { data } = await api.post(UBERTEST_RETIREMENT_APPLY_ENDPOINT, {
        plan_token: plan.plan_token,
        confirmation_text: ucTyped.trim(),
        typed_tenant_slug: ucTypedSlug.trim(),
      });
      if (!sameUcCtx(ctx)) return;
      setUcResult(data && typeof data === "object" ? data : { outcome: "unreadable" });
      setUcTyped(""); setUcTypedSlug("");
    } catch (err) {
      if (!sameUcCtx(ctx)) return;
      if (err?.response) {
        const message = sanitizeUbertestRetirementError(
          err, "The campaign retirement was refused");
        setUcError(message);
        toast.error(message);
        setUcPreview(null);
      } else {
        // No HTTP response at all. Never resubmit: the verification
        // action below reads the immutable manifest and says whether the
        // transaction committed.
        setUcOutcome("ambiguous");
      }
    } finally {
      if (sameUcCtx(ctx)) setUcApplying(false);
    }
  };

  // Deliberately SEPARATE from apply and from any retry. It takes no
  // plan and cannot write.
  const runUbertestRetirementVerify = async () => {
    if (ucApplying || ucVerifying) return;
    if (!isSuperAdmin || !isTenantReady || !ucIsApprovedTenant) return;
    const ctx = { ...ucCtxRef.current };
    setUcVerifying(true);
    try {
      const { data } = await api.post(UBERTEST_RETIREMENT_VERIFY_ENDPOINT, {});
      if (!sameUcCtx(ctx)) return;
      setUcVerify(data && typeof data === "object" ? data : null);
    } catch (err) {
      if (!sameUcCtx(ctx)) return;
      setUcError(sanitizeUbertestRetirementError(
        err, "The campaign-retirement verification failed"));
    } finally {
      if (sameUcCtx(ctx)) setUcVerifying(false);
    }
  };

  const runResiduePreview = async (offset = 0) => {
    if (residuePreviewLoading) return;
    if (!isSuperAdmin || !residueCompanyId || !residueCatalogId) return;
    if (!isResiduePagingPermitted(residuePreview, offset)) return;

    // Bind this request to the identity it was issued under.
    residuePreviewCtxRef.current = {
      gen: residuePreviewCtxRef.current.gen + 1,
      slug: residueTenantSlug,
      companyId: residueCompanyId,
      catalogId: residueCatalogId,
    };
    const ctx = { ...residuePreviewCtxRef.current };

    setResiduePreviewLoading(true);
    setResiduePreviewError("");
    try {
      const { data } = await api.post("/internal-admin/uber-direct/residue-preview", {
        company_id: ctx.companyId,
        catalog_id: ctx.catalogId,
        limit: 100,
        offset,
      });
      if (!samePreviewCtx(ctx)) return;             // superseded — discard whole
      const problem = describeResiduePayloadProblem(data);
      if (problem) {
        setResiduePreview(null);
        setResiduePreviewError(problem);
        return;
      }
      setResiduePreview(data);
      setResidueOffset(offset);
    } catch (err) {
      if (!samePreviewCtx(ctx)) return;             // superseded — discard whole
      setResiduePreview(null);
      setResiduePreviewError(sanitizeResidueError(err, "Residue preview failed"));
      toast.error(sanitizeResidueError(err, "Residue preview failed"));
    } finally {
      // Guarded: a stale request must never clear a live one's spinner.
      if (samePreviewCtx(ctx)) setResiduePreviewLoading(false);
    }
  };

  // ------------- Mark as Internal Test: lookup, gating, submit -------------  // ------------- Mark as Internal Test: lookup, gating, submit -------------

  const runMarkLookup = async (e) => {
    e?.preventDefault();
    if (!isTenantReady) return;
    const code = markOrderCode.trim();
    if (!code) return;
    setMarkLookup(null); setMarkLookupError(""); setMarkOutcome("");
    setMarkDialogOpen(false); setMarkTypedOrderCode(""); setMarkAckChecked(false);
    setMarkLookupLoading(true);
    try {
      const { data } = await api.get(
        `/internal-admin/local-delivery/orders/by-code/${encodeURIComponent(code)}/for-marking`,
      );
      setMarkLookup(data);
    } catch (err) {
      setMarkLookupError(formatApiError(err));
      toast.error(formatApiError(err, "Order lookup failed"));
    } finally {
      setMarkLookupLoading(false);
    }
  };

  // Re-reads the SAME order by its own code — the authoritative refresh
  // used both after a successful mark and after an ambiguous outcome.
  // `silent` suppresses the loading spinner/toast for the ambiguous-outcome
  // path, where a second, more specific message is shown instead.
  const refreshMarkLookup = async ({ silent = false } = {}) => {
    if (!markLookup) return null;
    if (!silent) setMarkLookupLoading(true);
    try {
      const { data } = await api.get(
        `/internal-admin/local-delivery/orders/by-code/${encodeURIComponent(markLookup.order_code)}/for-marking`,
      );
      setMarkLookup(data);
      return data;
    } catch (err) {
      if (!silent) {
        setMarkLookupError(formatApiError(err));
        toast.error(formatApiError(err, "Refresh failed"));
      }
      return null;
    } finally {
      if (!silent) setMarkLookupLoading(false);
    }
  };

  const markBlockingReasons = useMemo(
    () => computeMarkBlockingReasons({ role: user?.role, markLookup }),
    [user?.role, markLookup],
  );
  const canOpenMarkDialog = isTenantReady && markBlockingReasons.length === 0;
  const markTypedMatches = markLookup && markTypedOrderCode.trim() === markLookup.order_code;
  const canSendMark = canOpenMarkDialog && markAckChecked && markTypedMatches && !markSubmitting;

  const runMark = async () => {
    if (!canSendMark || !markLookup) return;
    setMarkSubmitting(true);
    setMarkOutcome("");
    try {
      await api.post(
        `/internal-admin/local-delivery/${markLookup.company_id}/orders/${markLookup.order_id}/mark-internal-test`,
      );
      toast.success("Order marked as internal test.");
      setMarkDialogOpen(false);
      await refreshMarkLookup();
    } catch (err) {
      if (err?.response) {
        // A definite rejection from the server (401/403/404/409/…) —
        // never ambiguous. Show it, close the dialog, and refresh so the
        // operator sees the real current state rather than a stale one.
        toast.error(formatApiError(err, "Marking failed"));
        setMarkDialogOpen(false);
        await refreshMarkLookup();
      } else {
        // No HTTP response was received at all (network failure/timeout)
        // — the mutation may or may not have applied server-side. Never
        // resend the mutation; read authoritative state instead and let
        // THAT decide what happened.
        setMarkOutcome("ambiguous");
        const resolved = await refreshMarkLookup({ silent: true });
        if (resolved?.internal_test) {
          setMarkOutcome("confirmed_via_refresh");
          toast.message(
            "Result was unclear, but the authoritative order state now confirms marking succeeded.",
          );
          setMarkDialogOpen(false);
        } else {
          setMarkOutcome("unresolved");
          toast.error(
            resolved
              ? "Result was unclear and the order is still not marked. Do not retry — review manually before trying again."
              : "Result was unclear and authoritative state could not be confirmed. Do not retry — review manually.",
          );
        }
      }
    } finally {
      setMarkSubmitting(false);
    }
  };

  // ------------- Reconciliation-refund: lookup, gating, submit -------------
  // Two backend calls, deliberately: the by-code lookup this console
  // already uses for marking (`.../orders/by-code/{order_code}/for-
  // marking`) resolves `order_id`/`company_id` from a bare, human-
  // readable order code — cross-tenant-ambiguity-safe by construction,
  // reused rather than re-derived — then the reconciliation-refund
  // eligibility route (which requires an explicit `company_id`/
  // `order_id`, never a bare code) is called with those resolved
  // values. Never a new "by code" resolver written here.
  const runReconLookup = async (e) => {
    e?.preventDefault();
    const code = reconOrderCode.trim();
    if (!code) return;
    setReconLookup(null); setReconLookupError(""); setReconOutcome(""); setReconResult(null);
    setReconDialogOpen(false); setReconTypedOrderCode(""); setReconAckChecked(false);
    setReconLookupLoading(true);
    try {
      const { data: idLookup } = await api.get(
        `/internal-admin/local-delivery/orders/by-code/${encodeURIComponent(code)}/for-marking`,
      );
      const { data: eligibility } = await api.get(
        `/internal-admin/local-delivery/${idLookup.company_id}/orders/${idLookup.order_id}/` +
        "reconciliation-refund/eligibility",
      );
      setReconLookup(eligibility);
    } catch (err) {
      setReconLookupError(formatApiError(err));
      toast.error(formatApiError(err, "Order lookup failed"));
    } finally {
      setReconLookupLoading(false);
    }
  };

  // Re-reads eligibility for the SAME order — the authoritative refresh
  // used both after a definite outcome and after an ambiguous one.
  // `silent` suppresses the loading spinner/toast for the ambiguous-
  // outcome path, where a more specific message is shown instead.
  const refreshReconLookup = async ({ silent = false } = {}) => {
    if (!reconLookup) return null;
    if (!silent) setReconLookupLoading(true);
    try {
      const { data } = await api.get(
        `/internal-admin/local-delivery/${reconLookup.tenant_company_id}/orders/${reconLookup.order_id}/` +
        "reconciliation-refund/eligibility",
      );
      setReconLookup(data);
      return data;
    } catch (err) {
      if (!silent) {
        setReconLookupError(formatApiError(err));
        toast.error(formatApiError(err, "Refresh failed"));
      }
      return null;
    } finally {
      if (!silent) setReconLookupLoading(false);
    }
  };

  const reconBlockingReasons = useMemo(
    () => computeReconciliationRefundBlockingReasons({ role: user?.role, reconLookup }),
    [user?.role, reconLookup],
  );
  const canOpenReconDialog = reconBlockingReasons.length === 0;
  const reconTypedMatches = reconLookup && reconTypedOrderCode.trim() === reconLookup.order_code;
  const canSendRecon = canOpenReconDialog && reconAckChecked && reconTypedMatches && !reconSubmitting;

  const runReconRefund = async () => {
    if (!canSendRecon || !reconLookup) return;
    setReconSubmitting(true);
    setReconOutcome(""); setReconResult(null);
    try {
      const { data } = await api.post(
        `/internal-admin/local-delivery/${reconLookup.tenant_company_id}/orders/${reconLookup.order_id}/` +
        "reconciliation-refund/confirm",
        {
          reason_code: RECONCILIATION_REFUND_REASON_CODE,
          acknowledgement_text: RECONCILIATION_REFUND_CONFIRMATION_TEXT,
        },
      );
      setReconResult(data);
      setReconDialogOpen(false);
      if (data.outcome === "succeeded") {
        toast.success("Refund succeeded. Order and payment records now show refunded.");
      } else if (data.outcome === "failed") {
        toast.error("Refund definitively failed. See the outcome panel below.");
      } else {
        // "ambiguous_reconciliation_required" or "unknown" — an
        // application-level ambiguous result the backend itself
        // reported inside an ordinary 200 response. Never resubmit
        // automatically; re-read authoritative state instead.
        toast.message(
          "Provider result was ambiguous. Do not resubmit — verify directly in the Stripe Dashboard " +
          "before any further action.",
        );
      }
      await refreshReconLookup();
    } catch (err) {
      if (err?.response) {
        // A definite rejection from the server (401/403/404/409/422/…)
        // — never ambiguous. Show it, close the dialog, refresh so the
        // operator sees the real current state rather than a stale one.
        toast.error(formatApiError(err, "Reconciliation refund failed"));
        setReconDialogOpen(false);
        await refreshReconLookup();
      } else {
        // No HTTP response was received at all (network failure/
        // timeout) — the request may or may not have reached the
        // server, let alone Stripe. Never resend it; read authoritative
        // state instead and let THAT decide what happened. Identical
        // posture to `runMark`'s own network-ambiguity handling above.
        setReconOutcome("ambiguous");
        const resolved = await refreshReconLookup({ silent: true });
        if (resolved && !resolved.eligible) {
          // No longer eligible — either it succeeded (now refunded) or
          // it's correctly locked behind refund_state_not_clean/
          // not_reconciliation_required. Either way, safe: no blind
          // retry is possible from here regardless of what actually
          // happened server-side.
          setReconOutcome("confirmed_via_refresh");
          toast.message(
            "Result was unclear, but the authoritative order state now confirms this order is no " +
            "longer eligible for a repeat attempt.",
          );
        } else {
          setReconOutcome("unresolved");
          toast.error(
            "Result was unclear and the order still appears eligible. Do not retry — verify directly " +
            "in the Stripe Dashboard and review manually before doing anything else.",
          );
        }
      }
    } finally {
      setReconSubmitting(false);
    }
  };

  const runLookup = async (e) => {
    e?.preventDefault();
    if (!isTenantReady) return;
    const code = orderCode.trim();
    if (!code) return;
    resetForNewOrder();
    setLookupLoading(true);
    try {
      const { data } = await api.get(
        `/internal-admin/local-delivery/orders/by-code/${encodeURIComponent(code)}`,
      );
      setLookup(data);
    } catch (err) {
      setLookupError(formatApiError(err));
      toast.error(formatApiError(err, "Order lookup failed"));
    } finally {
      setLookupLoading(false);
    }
  };

  const runAttestation = async () => {
    if (!lookup) return;
    setAttestationLoading(true);
    try {
      const { data } = await api.post(
        `/internal-admin/local-delivery/${lookup.company_id}/uber-direct/test-credential-verification/confirm`,
        { acknowledgement_text: CREDENTIAL_ACK_TEXT },
      );
      setAttestation(data);
      toast.success("Credential TEST attestation recorded.");
    } catch (err) {
      toast.error(formatApiError(err, "Credential attestation failed"));
    } finally {
      setAttestationLoading(false);
    }
  };

  const runPreview = async () => {
    if (!lookup || !isTenantReady) return;
    setPreviewError(""); setPreview(null);
    setPreviewLoading(true);
    try {
      const { data } = await api.post(
        `/internal-admin/local-delivery/${lookup.company_id}/${lookup.local_delivery_id}/dispatch-authorization/preview`,
      );
      // `data.token` and `data.authorization_id` live in this component's
      // state only, for exactly as long as it takes to send the confirm
      // request below — never rendered, never logged.
      setPreview(data);
    } catch (err) {
      setPreviewError(formatApiError(err));
      toast.error(formatApiError(err, "Preview failed"));
    } finally {
      setPreviewLoading(false);
    }
  };

  const blockingReasons = useMemo(
    () => computeConfirmBlockingReasons({ role: user?.role, lookup, preview, nowMs, readiness }),
    [user?.role, lookup, preview, nowMs, readiness],
  );
  const canOpenConfirm = blockingReasons.length === 0;
  const confirmTypedMatches = lookup && confirmTypedOrderCode.trim() === lookup.order_code;
  const canSendConfirm = canOpenConfirm && confirmAckChecked && confirmTypedMatches && !confirmLoading;

  const runConfirm = async () => {
    if (!canSendConfirm || !preview || !lookup) return;
    setConfirmLoading(true);
    try {
      const { data } = await api.post(
        `/internal-admin/local-delivery/${lookup.company_id}/${lookup.local_delivery_id}/dispatch-authorization/confirm`,
        {
          authorization_id: preview.authorization_id,
          token: preview.token,
          confirmation_template_version: preview.confirmation_template_version,
          acknowledgement_text: AUTHORIZATION_ACK_TEXT,
        },
      );
      setConfirmResult(data);
      toast.message("Confirmation sent. Read the delivery detail below — this response alone is not authoritative.");
      setConfirmDialogOpen(false);
      await runDetail();
    } catch (err) {
      toast.error(formatApiError(err, "Confirmation failed"));
    } finally {
      // Cleared unconditionally, success or failure — this component
      // never sends the same one-time token twice.
      setPreview((p) => (p ? { ...p, token: null, authorization_id: null } : p));
      setConfirmLoading(false);
    }
  };

  const runDetail = async () => {
    if (!lookup) return;
    setDetailLoading(true);
    try {
      const { data } = await api.get(
        `/internal-admin/local-delivery/${lookup.company_id}/${lookup.local_delivery_id}`,
      );
      setDetail(data);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load delivery detail"));
    } finally {
      setDetailLoading(false);
    }
  };

  const providerDeliveryId = detail?.dispatch_attempts?.[0]?.provider_delivery_id || "";
  const detailStatus = detail?.local_delivery?.status || "";
  const isTerminalStop = detailStatus === "reconciliation_required" || detailStatus === "delivery_failed";
  const canPoll = !!providerDeliveryId && !isTerminalStop;

  const runPoll = async () => {
    if (!canPoll || !lookup) return;
    setPollLoading(true);
    try {
      const { data } = await api.post(
        `/internal-admin/local-delivery/${lookup.company_id}/${lookup.local_delivery_id}/poll`,
      );
      setPollResult(data);
    } catch (err) {
      toast.error(formatApiError(err, "Poll failed"));
    } finally {
      setPollLoading(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <InternalShell>
        <div className="max-w-xl mx-auto py-16 text-center" data-testid="dispatch-console-forbidden">
          <Lock className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <h1 className="font-display text-xl font-semibold">Super admin only</h1>
          <p className="text-sm text-muted-foreground mt-2">
            The controlled Uber Direct TEST dispatch console is restricted to <code>super_admin</code>.
            Every action on this page is independently enforced by the backend regardless of this
            page-level message.
          </p>
        </div>
      </InternalShell>
    );
  }

  return (
    <InternalShell>
      <div className="space-y-5 max-w-4xl" data-testid="dispatch-console">
        <div>
          <div className="flex items-center gap-2 text-amber-700 mb-1">
            <ShieldAlert className="w-4 h-4" />
            <span className="text-[11px] uppercase tracking-wider font-medium">
              Controlled TEST dispatch — sandbox only
            </span>
          </div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
            <Truck className="w-5 h-5" /> Uber Direct TEST Dispatch Console
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            One controlled Uber Direct TEST Create Delivery attempt per confirmation, for one
            already-paid internal-test order at a time. No retry is authorized.
          </p>
        </div>

        {/* -------- Tenant readiness (required before any order lookup) -------- */}
        <Card className="p-4" data-testid="dispatch-console-readiness-card">
          <div className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground mb-3">
            Tenant readiness — required before order lookup
          </div>
          <p className="text-xs text-muted-foreground mb-3 max-w-2xl">
            Read-only. Checks the tenant's own pickup and address-validation configuration —
            never Uber, Google, or Stripe — before any order is looked up. Do not create or pay
            for another sandbox test order until this passes.
          </p>
          <form onSubmit={runReadinessCheck} className="flex gap-2 items-end">
            <div className="flex-1">
              <Label htmlFor="dispatch-console-tenant-slug-input" className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Tenant slug
              </Label>
              <Input
                id="dispatch-console-tenant-slug-input"
                value={tenantSlug}
                onChange={(e) => setTenantSlug(e.target.value)}
                placeholder="ubertest"
                autoComplete="off"
                data-testid="dispatch-console-tenant-slug-input"
              />
            </div>
            <Button
              type="submit"
              disabled={readinessLoading || !tenantSlug.trim()}
              data-testid="dispatch-console-readiness-check-btn"
            >
              {readinessLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1.5" />}
              {readinessLoading ? "Checking…" : "Check readiness"}
            </Button>
          </form>
          {readinessError && (
            <div
              className="mt-3 text-sm text-rose-700 flex items-center gap-2"
              role="alert"
              data-testid="dispatch-console-readiness-error"
            >
              <AlertTriangle className="w-4 h-4" /> {readinessError}
            </div>
          )}

          {readiness && (
            <div className="mt-4 border rounded-lg p-4" data-testid="dispatch-console-readiness-summary">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[13px]">
                <SummaryField label="Tenant" value={readiness.tenant_name} />
                <SummaryField label="Environment" value={readiness.environment} />
                <SummaryField label="Provider" value={readiness.provider} />
                <SummaryField label="Checked at" value={readiness.checked_at} />
              </div>
              <div className="mt-3 pt-3 border-t border-dashed flex flex-wrap gap-2">
                <PresenceBadge ok={!!readiness.pickup_address_present} label="Pickup address" />
                <PresenceBadge ok={!!readiness.pickup_contact_present} label="Pickup contact" />
                <PresenceBadge ok={!!readiness.pickup_latitude_present} label="Pickup latitude" />
                <PresenceBadge ok={!!readiness.pickup_longitude_present} label="Pickup longitude" />
                <PresenceBadge ok={!!readiness.pickup_coordinates_complete} label="Pickup coordinates complete" />
                <PresenceBadge ok={!!readiness.local_delivery_enabled} label="Local delivery enabled" />
                <PresenceBadge ok={!!readiness.uber_direct_provider_enabled} label="Uber Direct provider enabled" />
                <PresenceBadge ok={!!readiness.quote_enabled} label="Quote enabled" />
                <PresenceBadge ok={!!readiness.dispatch_structurally_disabled} label="Dispatch structurally disabled" />
                <PresenceBadge ok={!!readiness.address_validation_enabled} label="Address validation enabled" />
                <PresenceBadge
                  ok={readiness.geocoding_provider === "google_address_validation"}
                  label={`Geocoding provider: ${readiness.geocoding_provider ?? "—"}`}
                />
                <PresenceBadge
                  ok={!!readiness.google_address_validation_api_key_present}
                  label="Google Address Validation key present"
                />
              </div>
              <div
                className={`mt-3 pt-3 border-t border-dashed text-sm flex items-center gap-2 ${
                  isTenantReady ? "text-emerald-700" : "text-rose-700"
                }`}
                role="status"
                aria-live="polite"
                data-testid="dispatch-console-readiness-overall"
              >
                {isTenantReady ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                {isTenantReady
                  ? "Tenant is ready — order lookup unlocked."
                  : "Tenant is NOT ready — order lookup, preview, and confirmation stay locked. Do not create or pay for another test order yet."}
              </div>
              {!isTenantReady && (
                <div className="mt-2 space-y-1" data-testid="dispatch-console-readiness-blocking-reasons">
                  {readinessBlockingReasons.map((r, i) => (
                    <div key={i} className="text-xs text-rose-700 flex items-center gap-2">
                      <XCircle className="w-3.5 h-3.5 shrink-0" /> {r}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* -------- Activation residue preview (READ-ONLY) -------- */}
        <Card
          className={`p-4 ${!isTenantReady ? "opacity-60" : ""}`}
          data-testid="residue-preview-card"
        >
          <div className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground mb-3">
            Activation residue preview — read-only
          </div>

          <div
            className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
            data-testid="residue-preview-readonly-warning"
            role="note"
          >
            <div className="flex items-start gap-2">
              <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <strong>Read-only.</strong> This dry run reports what a production activation
                would find. It changes nothing, contacts neither Uber nor Stripe, and does
                <strong> not</strong> authorize reconciliation, credential activation, or a first
                production order. Each of those is a separate decision.
              </div>
            </div>
          </div>

          <div
            className="mb-3 flex flex-wrap items-center gap-2 text-[11px]"
            data-testid="residue-preview-environment"
          >
            <Badge variant="outline">Tenant: {residueTenantName || "—"}</Badge>
            <Badge variant="outline" data-testid="residue-preview-environment-badge">
              Environment: {residueEnvironment || "unknown"}
            </Badge>
            <Badge variant="outline">Service: rudras-ai-uber-staging-v2</Badge>
          </div>

          {!isTenantReady && (
            <p className="text-xs text-muted-foreground" data-testid="residue-preview-locked">
              Check tenant readiness above first — the company is resolved from that slug by the
              server, never typed here.
            </p>
          )}

          {isTenantReady && (
            <>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label
                    htmlFor="residue-preview-catalog-select"
                    className="text-[11px] uppercase tracking-wider text-muted-foreground"
                  >
                    Catalog
                  </Label>
                  <select
                    id="residue-preview-catalog-select"
                    data-testid="residue-preview-catalog-select"
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                    value={residueCatalogId}
                    disabled={residueCatalogsLoading || !(residueCatalogs?.catalogs || []).length}
                    onChange={(e) => selectResidueCatalog(e.target.value)}
                  >
                    <option value="">
                      {residueCatalogsLoading ? "Loading catalogs…" : "Select a catalog…"}
                    </option>
                    {(residueCatalogs?.catalogs || []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {c.archived ? " (archived)" : ""}
                        {c.delivery_enabled ? "" : " — delivery disabled"}
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  type="button"
                  onClick={() => runResiduePreview(0)}
                  disabled={!canRunResiduePreview}
                  data-testid="residue-preview-run-btn"
                >
                  {residuePreviewLoading
                    ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    : <Search className="w-3.5 h-3.5 mr-1.5" />}
                  {residuePreviewLoading ? "Running…" : "Run read-only preview"}
                </Button>
              </div>

              {residueCatalogsError && (
                <div
                  className="mt-3 text-sm text-rose-700 flex items-center gap-2"
                  role="alert"
                  data-testid="residue-preview-catalogs-error"
                >
                  <XCircle className="w-4 h-4" /> {residueCatalogsError}
                </div>
              )}

              {residueCatalogs && residueCatalogs.listing_complete === false && (
                <div
                  className="mt-3 rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900"
                  role="alert"
                  data-testid="residue-preview-catalogs-truncated"
                >
                  <strong>STOP.</strong> This tenant has more catalogs than the selector will
                  list ({residueCatalogs.selector_ceiling}). No partial list is shown. Escalate
                  rather than guessing a catalog.
                </div>
              )}

              {residuePreviewError && (
                <div
                  className="mt-3 text-sm text-rose-700 flex items-center gap-2"
                  role="alert"
                  data-testid="residue-preview-error"
                >
                  <XCircle className="w-4 h-4" /> {residuePreviewError}
                </div>
              )}

              {residuePreview && !residueInventoryComplete && (
                <div
                  className="mt-3 rounded-md border-2 border-rose-500 bg-rose-50 p-3 text-sm text-rose-900"
                  role="alert"
                  data-testid="residue-preview-stop"
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      <strong>STOP — inventory incomplete.</strong> This scan exceeded its
                      ceiling{residuePreview.scan_ceiling ? ` (${residuePreview.scan_ceiling})` : ""},
                      so it is not an inventory of this tenant.
                      {residuePreview.stop_reason
                        ? <> Reason: <code>{residuePreview.stop_reason}</code>.</>
                        : null}
                      {" "}No records and no counts are shown, and paging is disabled — changing
                      the offset cannot reach past the ceiling. Do not treat this as a clean
                      result. Escalate to the Product Owner.
                    </div>
                  </div>
                </div>
              )}

              {residuePreview && residueInventoryComplete && (
                <div className="mt-4 space-y-4" data-testid="residue-preview-results">
                  {/* The aggregate verdict, not the completeness badge, is
                      the headline. "Inventory complete" only means the scan
                      finished — it says nothing about whether anything
                      blocks, and must never be read as permission. */}
                  <div
                    className={
                      residueOutcome.kind === RESIDUE_OUTCOME_CLEAN
                        ? "rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900"
                        : "rounded-md border-2 border-rose-500 bg-rose-50 p-3 text-sm text-rose-900"
                    }
                    role="status"
                    data-testid="residue-preview-outcome"
                    data-outcome={residueOutcome.kind}
                  >
                    <div className="flex items-start gap-2">
                      {residueOutcome.kind === RESIDUE_OUTCOME_CLEAN
                        ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                        : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
                      <div>
                        <strong>{residueOutcome.text}</strong>
                        <div className="mt-1 text-xs">
                          A completed preview is evidence for the Product Owner's next decision.
                          It does not authorize reconciliation, activation or a production order.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 text-[11px]">
                    <Badge variant="outline" data-testid="residue-preview-scope">
                      Scope: {residuePreview.residue_scope || "unknown"}
                    </Badge>
                    <Badge variant="outline" data-testid="residue-preview-complete">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Scan complete
                    </Badge>
                    <Badge variant="outline">
                      Provider mode: {residuePreview.current_provider_mode || "unknown"}
                    </Badge>
                    {/* Rendered ONLY from figures `describeResiduePageProblem`
                        has already accepted, so no invented precision can
                        reach the operator. If they were unreadable the card
                        would be showing the error state, not this badge. */}
                    {!describeResiduePageProblem(residuePreview.page) && (
                      <Badge variant="outline" data-testid="residue-preview-page">
                        Showing {residuePreview.page.returned} of {residuePreview.page.total}
                      </Badge>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Residue is <strong>company-wide</strong>, not catalog-scoped — the catalog is
                    validated against this company, never used as a filter. That matches what the
                    activation guard itself enforces.
                  </p>

                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                      Blocker counts
                    </div>
                    <div className="flex flex-wrap gap-2" data-testid="residue-preview-blocker-counts">
                      {Object.entries(residuePreview.blocker_counts || {}).map(([k, v]) => (
                        <Badge
                          key={k}
                          variant={v ? "destructive" : "outline"}
                          data-testid={`residue-blocker-${k}`}
                        >
                          {k}: {formatResidueCount(v)}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                      Category counts
                    </div>
                    <div className="flex flex-wrap gap-2" data-testid="residue-preview-category-counts">
                      {Object.entries(residuePreview.category_counts || {}).map(([k, v]) => {
                        const meta = RESIDUE_CATEGORY_LABELS[k];
                        return (
                          <Badge
                            key={k}
                            variant={meta && meta.blocking === false ? "outline" : "destructive"}
                            data-testid={`residue-category-${k}`}
                            title={meta?.detail || "Unrecognised category — treated as blocking."}
                          >
                            {meta?.label || k}: {formatResidueCount(v)}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>

                  {(residuePreview.blocking_order_codes || []).length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                        Blocking order codes
                      </div>
                      <div
                        className="text-xs font-mono break-all"
                        data-testid="residue-preview-blocking-codes"
                      >
                        {(residuePreview.blocking_order_codes || []).join(", ")}
                      </div>
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="residue-preview-records">
                      <thead>
                        <tr className="text-left text-muted-foreground">
                          <th className="py-1 pr-3">Order</th>
                          <th className="py-1 pr-3">Classification</th>
                          <th className="py-1 pr-3">Blockers</th>
                          <th className="py-1 pr-3">Delivery</th>
                          <th className="py-1 pr-3">Payment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(residuePreview.records || []).map((r) => {
                          const meta = RESIDUE_CATEGORY_LABELS[r.reconciliation_category];
                          return (
                            <tr
                              key={r.order_id || r.order_code}
                              className="border-t align-top"
                              data-testid={`residue-record-${r.order_code}`}
                            >
                              <td className="py-1.5 pr-3 font-mono">
                                {r.order_code}
                                {r.internal_test ? (
                                  <Badge variant="outline" className="ml-1">internal_test</Badge>
                                ) : null}
                              </td>
                              <td className="py-1.5 pr-3">
                                <div className="font-medium">
                                  {meta?.label || r.reconciliation_category}
                                </div>
                                <div className="text-muted-foreground">
                                  {meta?.detail || "Unrecognised category — treated as blocking."}
                                </div>
                                {r.external_action_required ? (
                                  <Badge variant="destructive" className="mt-1">
                                    Requires action at Uber or Stripe
                                  </Badge>
                                ) : null}
                              </td>
                              <td className="py-1.5 pr-3">
                                {(r.blockers || []).length
                                  ? (r.blockers || []).join(", ")
                                  : "none"}
                              </td>
                              <td className="py-1.5 pr-3">
                                {r.delivery_status || "—"}
                                {r.delivery_mode ? ` (${r.delivery_mode})` : ""}
                                {r.provider_delivery_id_present ? " · provider-backed" : ""}
                              </td>
                              <td className="py-1.5 pr-3">
                                {r.payment_classification || "—"}
                                {r.payment_could_still_capture ? " · can still capture" : ""}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {!(residuePreview.records || []).length && (
                      <p
                        className="text-xs text-muted-foreground py-2"
                        data-testid="residue-preview-empty"
                      >
                        No Uber Direct order records on this page. The counts above are the
                        authoritative summary.
                      </p>
                    )}
                  </div>

                  {!describeResiduePageProblem(residuePreview.page) && (
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const target = nextResidueOffset(residuePreview, "prev");
                        if (target !== null) runResiduePreview(target);
                      }}
                      disabled={residuePreviewLoading || !canPageResidueBackward(residuePreview)}
                      data-testid="residue-preview-prev-btn"
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const target = nextResidueOffset(residuePreview, "next");
                        if (target !== null) runResiduePreview(target);
                      }}
                      disabled={residuePreviewLoading || !canPageResidueForward(residuePreview)}
                      data-testid="residue-preview-next-btn"
                    >
                      Next
                    </Button>
                    <span className="text-[11px] text-muted-foreground">
                      Paging is available only because this inventory is complete.
                    </span>
                  </div>
                  )}
                </div>
              )}
            </>
          )}
        </Card>

        {/* -------- Local-residue reconciliation — preview, then apply (Sep 2026) -------- */}
        <Card
          className={`p-4 ${!isTenantReady ? "opacity-60" : ""}`}
          data-testid="local-reconciliation-card"
        >
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-medium text-muted-foreground mb-3">
            <ShieldAlert className="w-3.5 h-3.5" />
            Local-residue reconciliation — preview, then apply
          </div>
          <p className="text-xs text-muted-foreground mb-2 max-w-2xl">
            Closes ONLY abandoned local-only test residue: a paid order whose Uber TEST delivery was
            quoted and never dispatched — no dispatch attempt, no provider delivery id, no provider
            event, no reusable quote. Each eligible delivery is moved to the administrative terminal
            state <span className="font-mono">reconciled_local_residue</span> with a full audit record.
            The order's state and payment are never changed; no Stripe or Uber call is made; nothing
            is deleted. Unsettled payments and provider-backed deliveries are hazards: they are listed,
            counted separately and structurally excluded from every plan.
          </p>
          <p className="text-xs font-medium text-amber-800 mb-3 max-w-2xl" data-testid="local-reconciliation-scope">
            {LOCAL_RECONCILIATION_SCOPE_SENTENCE}
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={runLocalReconciliationPreview}
              disabled={lrLoading || lrApplying || !isSuperAdmin || !isTenantReady || !lrCompanyId || !lrEnvironment}
              data-testid="local-reconciliation-preview-btn"
            >
              {lrLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1.5" />}
              {lrLoading ? "Previewing…" : "Run reconciliation preview (read-only)"}
            </Button>
            {lrEnvironment && (
              <Badge variant={lrEnvironment === "sandbox" ? "outline" : "destructive"} data-testid="local-reconciliation-env-badge">
                {lrEnvironment === "sandbox" ? "SANDBOX" : `ENVIRONMENT: ${lrEnvironment}`}
              </Badge>
            )}
          </div>

          {lrError && (
            <div className="mt-3 text-sm text-rose-700 flex items-center gap-2" role="alert" data-testid="local-reconciliation-error">
              <AlertTriangle className="w-4 h-4" /> {lrError}
            </div>
          )}

          {lrPreview && lrPreview.truncated === true && (
            <div className="mt-3 border rounded-lg p-3 text-sm text-rose-700 flex items-center gap-2" role="alert" data-testid="local-reconciliation-truncated">
              <XCircle className="w-4 h-4 shrink-0" />
              STOP — inventory incomplete: the scan exceeded {formatLocalReconciliationCount(lrPreview.scan_ceiling)} records.
              No plan was minted and nothing can be applied.
            </div>
          )}

          {lrPreview && lrPreview.truncated === false && (
            <div className="mt-4 border rounded-lg p-4" data-testid="local-reconciliation-summary">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Badge variant="outline">Read-only preview</Badge>
                <Badge variant="outline">Inventory complete</Badge>
                <span className="text-[11px] text-muted-foreground">
                  {formatLocalReconciliationCount(lrPreview.total)} Uber orders classified · classifier {lrPreview.classifier_version}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-[13px]" data-testid="local-reconciliation-counts">
                {LOCAL_RECONCILIATION_CATEGORY_ORDER.map((key) => (
                  <div key={key} data-testid={`local-reconciliation-count-${key}`}>
                    <div className="text-[9.5px] uppercase tracking-wider text-muted-foreground">
                      {LOCAL_RECONCILIATION_CATEGORY_LABELS[key]}
                    </div>
                    <div className={`mt-0.5 font-medium ${LOCAL_RECONCILIATION_HAZARD_CATEGORIES.includes(key) ? "text-rose-700" : "text-foreground"}`}>
                      {formatLocalReconciliationCount(lrPreview.category_counts?.[key])}
                    </div>
                  </div>
                ))}
              </div>

              {lrPreview.candidates.length > 0 && (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-xs" data-testid="local-reconciliation-table">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                        <th className="py-1 pr-3">Order</th>
                        <th className="py-1 pr-3">Delivery status</th>
                        <th className="py-1 pr-3">Category</th>
                        <th className="py-1 pr-3">Reasons</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lrPreview.candidates.map((rec, i) => (
                        <tr
                          key={`${rec.order_code || "order"}-${i}`}
                          className={rec.hazard ? "text-rose-700" : ""}
                          data-testid={`local-reconciliation-row-${rec.category}`}
                        >
                          <td className="py-1 pr-3 font-mono">{rec.order_code || "—"}</td>
                          <td className="py-1 pr-3 font-mono">{rec.delivery_status || "—"}</td>
                          <td className="py-1 pr-3">
                            {LOCAL_RECONCILIATION_CATEGORY_LABELS[rec.category] || "Unknown — fail closed"}
                            {rec.hazard ? " — EXCLUDED" : ""}
                          </td>
                          <td className="py-1 pr-3 font-mono">{Array.isArray(rec.reasons) ? rec.reasons.join(", ") : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {lrResult ? (
                <div className="mt-3 pt-3 border-t border-dashed" data-testid="local-reconciliation-result">
                  {lrResult.outcome === "applied" ? (
                    <div className="text-sm text-emerald-700 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 shrink-0" />
                      Applied — {formatLocalReconciliationCount(lrResult.summary?.reconciled)} closed,{" "}
                      {formatLocalReconciliationCount(lrResult.summary?.already_reconciled)} already closed.
                      Run the activation residue preview above to confirm the new inventory.
                    </div>
                  ) : lrResult.outcome === "stopped_partial" ? (
                    <div className="text-sm text-amber-800 flex items-center gap-2" role="alert">
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      Stopped part-way — {formatLocalReconciliationCount(lrResult.summary?.reconciled)} closed,{" "}
                      {formatLocalReconciliationCount(lrResult.summary?.precondition_changed)} changed underneath,{" "}
                      {formatLocalReconciliationCount(lrResult.summary?.evidence_write_failed)} evidence write failed,{" "}
                      {formatLocalReconciliationCount(lrResult.summary?.not_attempted)} not attempted.
                      Nothing was retried. Run the reconciliation preview again before any further action.
                    </div>
                  ) : (
                    <div className="text-sm text-rose-700 flex items-center gap-2" role="alert">
                      <XCircle className="w-4 h-4 shrink-0" />
                      The server's reply could not be read. Run the reconciliation preview again; it reports what is now closed.
                    </div>
                  )}
                  {Array.isArray(lrResult.results) && lrResult.results.length > 0 && (
                    <ul className="mt-2 text-xs font-mono space-y-0.5" data-testid="local-reconciliation-result-rows">
                      {lrResult.results.map((r, i) => (
                        <li key={`${r.order_code || "order"}-${i}`}>{r.order_code || "—"} · {r.result}</li>
                      ))}
                    </ul>
                  )}
                  <p className="mt-2 text-xs text-amber-800">{LOCAL_RECONCILIATION_SCOPE_SENTENCE}</p>
                </div>
              ) : lrOutcome === "ambiguous" ? (
                <div className="mt-3 pt-3 border-t border-dashed text-sm text-amber-800 flex items-center gap-2" role="alert" data-testid="local-reconciliation-ambiguous">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  No response was received. Do not resubmit — run the reconciliation preview again; it reports what is now closed.
                </div>
              ) : (
                <div className="mt-3 pt-3 border-t border-dashed space-y-3" data-testid="local-reconciliation-apply-block">
                  {lrPreview.plan && lrPreview.category_counts?.eligible_local_reconciliation > 0 ? (
                    <>
                      <div className="text-xs text-muted-foreground max-w-2xl">
                        Plan <span className="font-mono">{lrPreview.plan.plan_id}</span> covers exactly{" "}
                        {formatLocalReconciliationCount(lrPreview.plan.candidate_count)} eligible record(s) as they were at
                        preview time and expires at <span className="font-mono">{lrPreview.plan.expires_at}</span>.
                        The server re-checks every record before writing and refuses the whole plan if anything changed.
                        The {formatLocalReconciliationCount(
                          (lrPreview.category_counts?.operator_action_required || 0)
                          + (lrPreview.category_counts?.provider_truth_required || 0))} hazard record(s) above are not in this plan and cannot be.
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          {/* The label's own class is `uppercase`, which
                              CSS-transforms whatever it wraps. The slug
                              is case-SENSITIVE and lowercase, so it
                              would have rendered as "UBERTEST" beside a
                              validator that only accepts "ubertest" —
                              the operator reads one thing and must type
                              another. `normal-case` on the value (the
                              same treatment the confirmation text
                              already had) is the fix. */}
                          <Label htmlFor="local-reconciliation-typed-slug" className="text-[11px] uppercase tracking-wider text-muted-foreground">
                            Type the tenant slug (
                            <span className="font-mono normal-case">{lrTenantSlug}</span>
                            )
                          </Label>
                          <Input
                            id="local-reconciliation-typed-slug"
                            value={lrTypedSlug}
                            onChange={(e) => setLrTypedSlug(e.target.value)}
                            autoComplete="off"
                            disabled={lrApplying}
                            data-testid="local-reconciliation-typed-slug"
                          />
                        </div>
                        <div>
                          <Label htmlFor="local-reconciliation-typed-confirmation" className="text-[11px] uppercase tracking-wider text-muted-foreground">
                            Type exactly: <span className="font-mono normal-case">{lrPreview.plan.confirmation_text_required}</span>
                          </Label>
                          <Input
                            id="local-reconciliation-typed-confirmation"
                            value={lrTyped}
                            onChange={(e) => setLrTyped(e.target.value)}
                            autoComplete="off"
                            disabled={lrApplying}
                            data-testid="local-reconciliation-typed-confirmation"
                          />
                        </div>
                      </div>
                    </>
                  ) : null}
                  {lrBlockingReasons.length > 0 && (
                    <div className="space-y-1" data-testid="local-reconciliation-blocking-reasons">
                      {lrBlockingReasons.map((r, i) => (
                        <div key={i} className="text-xs text-rose-700 flex items-center gap-2">
                          <Lock className="w-3.5 h-3.5 shrink-0" /> {r}
                        </div>
                      ))}
                    </div>
                  )}
                  {lrPreview.plan && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={applyLocalReconciliation}
                      disabled={!canApplyLocalReconciliation}
                      data-testid="local-reconciliation-apply-btn"
                    >
                      {lrApplying ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ShieldAlert className="w-3.5 h-3.5 mr-1.5" />}
                      {lrApplying ? "Applying…" : "Apply this plan — closes eligible records only"}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* -------- One-time UBERTEST campaign retirement (Sep 2026) -------- */}
        <Card
          className={`p-4 border-amber-300 ${!ucIsApprovedTenant ? "opacity-60" : ""}`}
          data-testid="ubertest-campaign-retirement-card"
        >
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-medium text-muted-foreground mb-3">
            <ShieldAlert className="w-3.5 h-3.5" />
            One-time UBERTEST campaign retirement
          </div>

          <div className="text-xs text-muted-foreground space-y-1 mb-3">
            <p>
              Retires the approved one-time UBERTEST sandbox test campaign — all{" "}
              <span className="font-mono">{UBERTEST_RETIREMENT_CAMPAIGN_SIZE}</span> records,
              or none. It adds one administrative marker to each order and delivery and
              records one immutable campaign manifest, inside a single transaction.
            </p>
            <p className="font-medium text-foreground">
              {UBERTEST_RETIREMENT_PRESERVATION_SENTENCE}
            </p>
            <p>
              It <strong>retires</strong>; it never deletes. No delivery status, order state
              or payment label is changed. No Stripe or Uber call is made. A record that was{" "}
              <span className="font-mono">courier_requested</span> stays{" "}
              <span className="font-mono">courier_requested</span>.
            </p>
            <p>{UBERTEST_RETIREMENT_COMPLETENESS_SENTENCE}</p>
            <p>{UBERTEST_RETIREMENT_INDEPENDENT_SENTENCE}</p>
            <p className="font-medium text-foreground">
              {UBERTEST_RETIREMENT_SCOPE_SENTENCE}
            </p>
            <p>
              Bound to{" "}
              <span className="font-mono normal-case">
                {UBERTEST_RETIREMENT_TENANT_SLUG} / {UBERTEST_RETIREMENT_ENVIRONMENT}
              </span>{" "}
              only, resolved by the server. This card sends no company, environment or
              order code.
            </p>
          </div>

          {!ucIsApprovedTenant && (
            <div
              className="text-xs text-muted-foreground mb-3"
              data-testid="ubertest-campaign-retirement-inert"
            >
              Inert here — this control acts only on the{" "}
              <span className="font-mono normal-case">{UBERTEST_RETIREMENT_TENANT_SLUG}</span>{" "}
              tenant in{" "}
              <span className="font-mono normal-case">{UBERTEST_RETIREMENT_ENVIRONMENT}</span>.
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={runUbertestRetirementPreview}
              disabled={ucLoading || ucApplying || !isSuperAdmin || !isTenantReady
                || !ucIsApprovedTenant}
              data-testid="ubertest-campaign-retirement-preview-btn"
            >
              {ucLoading
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <Search className="w-3.5 h-3.5 mr-1.5" />}
              {ucLoading ? "Previewing…" : "Run campaign preview (read-only)"}
            </Button>
            {/* Separate from apply and from any retry, on purpose. */}
            <Button
              size="sm"
              variant="outline"
              onClick={runUbertestRetirementVerify}
              disabled={ucVerifying || ucApplying || !isSuperAdmin || !isTenantReady
                || !ucIsApprovedTenant}
              data-testid="ubertest-campaign-retirement-verify-btn"
            >
              {ucVerifying
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <Search className="w-3.5 h-3.5 mr-1.5" />}
              {ucVerifying ? "Verifying…" : "Verify campaign (read-only)"}
            </Button>
          </div>

          {ucError && (
            <div
              className="mt-3 text-xs text-destructive"
              data-testid="ubertest-campaign-retirement-error"
            >
              {ucError}
            </div>
          )}

          {ucOutcome === "ambiguous" && (
            <div
              className="mt-3 text-xs text-destructive font-medium"
              data-testid="ubertest-campaign-retirement-ambiguous"
            >
              No response was received. DO NOT resubmit. The retirement is one
              transaction, so it either committed or it did not — run
              “Verify campaign (read-only)” to find out which.
            </div>
          )}

          {ucPreview && ucPreview.truncated === true && (
            <div
              className="mt-3 text-xs text-destructive font-medium"
              data-testid="ubertest-campaign-retirement-truncated"
            >
              STOP — inventory incomplete. The scan exceeded its ceiling, no plan exists,
              and nothing can be retired.
            </div>
          )}

          {ucPreview && ucPreview.campaign_completed === true && (
            <div
              className="mt-3 text-xs font-medium"
              data-testid="ubertest-campaign-retirement-completed"
            >
              This campaign is already retired. This control is now verification-only and
              can never mint another plan.
            </div>
          )}

          {ucPreview && ucPreview.truncated === false && (
            <div className="mt-3 space-y-3">
              <div
                className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs"
                data-testid="ubertest-campaign-retirement-counts"
              >
                <div>
                  <div className="text-muted-foreground">Approved campaign</div>
                  <div className="font-mono">
                    {ucPreview.campaign_inventory?.approved}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Present</div>
                  <div className="font-mono">{ucPreview.campaign_inventory?.present}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Retirement-eligible</div>
                  <div className="font-mono">
                    {ucPreview.campaign_inventory?.retirement_eligible}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Unknown / mismatched</div>
                  <div className="font-mono">
                    {ucPreview.campaign_inventory?.unknown_or_mismatched}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Missing approved</div>
                  <div className="font-mono">
                    {(ucPreview.campaign_inventory?.missing_approved_codes || []).length}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Duplicated approved</div>
                  <div className="font-mono">
                    {(ucPreview.campaign_inventory?.duplicate_approved_codes || []).length}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Blockers outside campaign</div>
                  <div className="font-mono">
                    {(ucPreview.campaign_inventory
                      ?.extra_activation_blockers_outside_campaign || []).length}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Evidence retained</div>
                  <div className="font-mono">{ucPreview.evidence_retained_total}</div>
                </div>
              </div>

              {Array.isArray(ucPreview.plan_blocked_reasons)
                && ucPreview.plan_blocked_reasons.length > 0 && (
                <div
                  className="text-xs text-destructive"
                  data-testid="ubertest-campaign-retirement-plan-blocked"
                >
                  No plan was minted. A subset is never retired.
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                      <th className="py-1 pr-3">Order</th>
                      <th className="py-1 pr-3">Membership</th>
                      <th className="py-1 pr-3">Historical category (preserved)</th>
                      <th className="py-1 pr-3">Delivery status (unchanged)</th>
                      <th className="py-1 pr-3">Payment label (unchanged)</th>
                      <th className="py-1 pr-3">Reasons</th>
                      <th className="py-1 pr-3">Action</th>
                    </tr>
                  </thead>
                  <tbody data-testid="ubertest-campaign-retirement-records">
                    {(ucPreview.records || []).map((r) => (
                      <tr key={r.order_code} className="border-t border-border/40">
                        <td className="py-1 pr-3 font-mono">{r.order_code}</td>
                        <td className="py-1 pr-3">
                          {UBERTEST_RETIREMENT_MEMBERSHIP_LABELS[r.membership] || r.membership}
                          {UBERTEST_RETIREMENT_EXCLUDED_MEMBERSHIPS.includes(r.membership) && (
                            <span className="ml-1 font-semibold text-destructive">EXCLUDED</span>
                          )}
                        </td>
                        <td className="py-1 pr-3">
                          {UBERTEST_RETIREMENT_HISTORICAL_LABELS[r.historical_category]
                            || r.historical_category || "—"}
                          {r.evidence_retained && (
                            <span className="ml-1 font-semibold">RETAINED</span>
                          )}
                          {r.legacy_test_marker_absent && (
                            <span className="ml-1">· legacy_test_marker_absent</span>
                          )}
                        </td>
                        <td className="py-1 pr-3 font-mono">
                          {r.preserved_delivery_status || "—"}
                        </td>
                        <td className="py-1 pr-3 font-mono">
                          {r.preserved_payment_status || "—"}
                        </td>
                        {/* Sanitized classifier reason identifiers only —
                            never an id, amount, address, payment or
                            provider value. */}
                        <td
                          className="py-1 pr-3 font-mono break-words max-w-[18rem]"
                          data-testid={`ubertest-campaign-retirement-reasons-${r.order_code}`}
                        >
                          {formatUbertestRetirementReasons(r.reasons)}
                        </td>
                        <td className="py-1 pr-3 font-mono">{r.disposition}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {ucPreview.plan && !ucResult && ucOutcome === "" && (
                <div className="space-y-2 border-t border-border/40 pt-3">
                  <div className="text-xs text-muted-foreground">
                    This plan covers exactly{" "}
                    <span className="font-mono">{ucPreview.plan.candidate_count}</span>{" "}
                    records and expires at{" "}
                    <span className="font-mono">{ucPreview.plan.expires_at}</span>. The
                    server re-reads and re-classifies every record before writing; one
                    mismatch refuses the whole plan with zero writes.
                  </div>

                  <div className="grid gap-2 md:grid-cols-2">
                    <div>
                      <Label
                        htmlFor="ubertest-retirement-typed-slug"
                        className="text-[11px] uppercase tracking-wider text-muted-foreground"
                      >
                        Type the tenant slug (
                        <span className="font-mono normal-case">
                          {UBERTEST_RETIREMENT_TENANT_SLUG}
                        </span>
                        )
                      </Label>
                      <Input
                        id="ubertest-retirement-typed-slug"
                        value={ucTypedSlug}
                        onChange={(e) => setUcTypedSlug(e.target.value)}
                        disabled={ucApplying}
                        className="font-mono normal-case"
                        data-testid="ubertest-campaign-retirement-typed-slug"
                      />
                    </div>
                    <div>
                      <Label
                        htmlFor="ubertest-retirement-typed-confirmation"
                        className="text-[11px] uppercase tracking-wider text-muted-foreground"
                      >
                        Type the confirmation (
                        <span className="font-mono normal-case">
                          {ucPreview.plan.confirmation_text_required}
                        </span>
                        )
                      </Label>
                      <Input
                        id="ubertest-retirement-typed-confirmation"
                        value={ucTyped}
                        onChange={(e) => setUcTyped(e.target.value)}
                        disabled={ucApplying}
                        className="font-mono normal-case"
                        data-testid="ubertest-campaign-retirement-typed-confirmation"
                      />
                    </div>
                  </div>

                  {ucBlockingReasons.length > 0 && (
                    <ul
                      className="text-xs text-muted-foreground list-disc pl-4"
                      data-testid="ubertest-campaign-retirement-blocking-reasons"
                    >
                      {ucBlockingReasons.map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  )}

                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={applyUbertestRetirement}
                    disabled={!canApplyUbertestRetirement}
                    data-testid="ubertest-campaign-retirement-apply-btn"
                  >
                    {ucApplying
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <ShieldAlert className="w-3.5 h-3.5 mr-1.5" />}
                    {ucApplying
                      ? "Retiring…"
                      : `Retire the campaign — all ${UBERTEST_RETIREMENT_CAMPAIGN_SIZE} records, in one transaction`}
                  </Button>
                </div>
              )}
            </div>
          )}

          {ucResult && (
            <div
              className="mt-3 text-xs space-y-1"
              data-testid="ubertest-campaign-retirement-result"
            >
              <div>
                Outcome: <span className="font-mono">{ucResult.outcome}</span> · records{" "}
                <span className="font-mono">{ucResult.record_count}</span> · deletions{" "}
                <span className="font-mono">{ucResult.deletions}</span>
              </div>
              <div className="text-muted-foreground">{ucResult.next_step}</div>
              <div className="font-medium">{UBERTEST_RETIREMENT_SCOPE_SENTENCE}</div>
            </div>
          )}

          {ucVerify && (
            <div
              className="mt-3 text-xs space-y-1"
              data-testid="ubertest-campaign-retirement-verify-result"
            >
              <div>
                Committed: <span className="font-mono">{String(ucVerify.committed)}</span>{" "}
                · campaign retired:{" "}
                <span className="font-mono">{String(ucVerify.campaign_retired)}</span> ·
                deletions <span className="font-mono">{ucVerify.deletions}</span>
              </div>
              <div className="text-muted-foreground">{ucVerify.conclusion}</div>
            </div>
          )}
        </Card>

        {/* -------- Mark as Internal Test (before payment) -------- */}
        <Card className={`p-4 ${!isTenantReady ? "opacity-60" : ""}`} data-testid="dispatch-console-mark-card">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-medium text-muted-foreground mb-3">
            <Tag className="w-3.5 h-3.5" /> Prepare a new order — mark as internal test before payment
          </div>
          <p className="text-xs text-muted-foreground mb-3 max-w-2xl">
            For a new, still-unpaid Local Delivery order: complete checkout and let the Uber TEST
            delivery quote appear automatically. Before clicking Accept &amp; Pay, copy the order
            reference shown on that quote screen, look it up here, and mark it as an internal test.
            Marking never accepts the quote, never creates a Stripe payment session, and never calls
            Uber — it only tags the order while payment remains pending. An already-paid order can
            never be marked here or anywhere else.
          </p>
          {!isTenantReady && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-3" data-testid="dispatch-console-mark-locked">
              Locked until tenant readiness passes above.
            </p>
          )}
          <form onSubmit={runMarkLookup} className="flex gap-2 items-end">
            <div className="flex-1">
              <Label htmlFor="dispatch-console-mark-order-code-input" className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Order code
              </Label>
              <Input
                id="dispatch-console-mark-order-code-input"
                value={markOrderCode}
                onChange={(e) => setMarkOrderCode(e.target.value)}
                placeholder="UBE-ORD-000123"
                autoComplete="off"
                disabled={!isTenantReady}
                data-testid="dispatch-console-mark-order-code-input"
              />
            </div>
            <Button
              type="submit"
              disabled={markLookupLoading || !markOrderCode.trim() || !isTenantReady}
              data-testid="dispatch-console-mark-lookup-btn"
            >
              <Search className="w-3.5 h-3.5 mr-1.5" />
              {markLookupLoading ? "Looking up…" : "Look up"}
            </Button>
          </form>
          {markLookupError && (
            <div className="mt-3 text-sm text-rose-700 flex items-center gap-2" role="alert" data-testid="dispatch-console-mark-lookup-error">
              <AlertTriangle className="w-4 h-4" /> {markLookupError}
            </div>
          )}

          {markLookup && (
            <div className="mt-4 border rounded-lg p-4" data-testid="dispatch-console-mark-summary">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[13px]">
                <SummaryField label="Order code" value={markLookup.order_code} />
                <SummaryField label="Tenant" value={markLookup.tenant_name} />
                <SummaryField label="Amount" value={`${markLookup.amount ?? "—"} ${markLookup.currency || ""}`.trim()} />
                <SummaryField label="Payment status" value={markLookup.payment_status} />
              </div>

              {markLookup.internal_test ? (
                <div className="mt-3 pt-3 border-t border-dashed text-sm text-emerald-700 flex items-center gap-2" data-testid="dispatch-console-mark-already-marked">
                  <CheckCircle2 className="w-4 h-4" />
                  Already marked internal test
                  {markLookup.internal_test_marked_by ? ` by ${markLookup.internal_test_marked_by}` : ""}
                  {markLookup.internal_test_marked_at ? ` at ${markLookup.internal_test_marked_at}` : ""}.
                </div>
              ) : markBlockingReasons.length > 0 ? (
                <div className="mt-3 pt-3 border-t border-dashed space-y-1" data-testid="dispatch-console-mark-blocking-reasons">
                  {markBlockingReasons.map((r, i) => (
                    <div key={i} className="text-sm text-rose-700 flex items-center gap-2">
                      <XCircle className="w-4 h-4 shrink-0" /> {r}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-3 pt-3 border-t border-dashed">
                  <Button
                    onClick={() => setMarkDialogOpen(true)}
                    data-testid="dispatch-console-mark-open-btn"
                  >
                    <Tag className="w-3.5 h-3.5 mr-1.5" /> Mark as internal test
                  </Button>
                </div>
              )}

              {markOutcome === "ambiguous" && (
                <div className="mt-3 pt-3 border-t border-dashed text-sm text-amber-800 flex items-center gap-2" role="alert" data-testid="dispatch-console-mark-ambiguous">
                  <AlertTriangle className="w-4 h-4" /> Result unclear — checking authoritative order state now. Do not retry.
                </div>
              )}
              {markOutcome === "unresolved" && (
                <div className="mt-3 pt-3 border-t border-dashed text-sm text-rose-700 flex items-center gap-2 flex-wrap" role="alert" data-testid="dispatch-console-mark-unresolved">
                  <AlertTriangle className="w-4 h-4" /> Outcome could not be confirmed. Do not retry — refresh manually and review before trying again.
                  <Button variant="outline" size="sm" onClick={() => refreshMarkLookup()} data-testid="dispatch-console-mark-manual-refresh-btn">
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>

        <Dialog open={markDialogOpen} onOpenChange={(o) => { if (!o) setMarkDialogOpen(false); }}>
          <DialogContent data-testid="dispatch-console-mark-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5" /> Mark as internal test — {markLookup?.order_code}
              </DialogTitle>
              <DialogDescription>
                Order <span className="font-mono">{markLookup?.order_code}</span> is currently{" "}
                <span className="font-mono">payment_status={markLookup?.payment_status}</span>. Marking
                it internal-test prepares this unpaid sandbox order for the separately controlled TEST
                dispatch workflow below. This action does NOT charge any payment and does NOT contact
                Uber. A paid order can never be marked — this control disables itself the moment that
                becomes true.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={markAckChecked}
                  onChange={(e) => setMarkAckChecked(e.target.checked)}
                  data-testid="dispatch-console-mark-ack-checkbox"
                  className="mt-1"
                />
                <span>
                  I confirm this is a TEST/sandbox order and that marking it internal-test does not
                  process any payment or contact Uber.
                </span>
              </label>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Type the exact order code to confirm: <span className="font-mono">{markLookup?.order_code}</span>
                </Label>
                <Input
                  value={markTypedOrderCode}
                  onChange={(e) => setMarkTypedOrderCode(e.target.value)}
                  autoComplete="off"
                  data-testid="dispatch-console-mark-typed-order-code-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMarkDialogOpen(false)}>Cancel</Button>
              <Button
                disabled={!canSendMark}
                onClick={runMark}
                data-testid="dispatch-console-mark-send-btn"
              >
                {markSubmitting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                Mark as internal test
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* -------- Step 1: order selection -------- */}
        <Card className={`p-4 ${!isTenantReady ? "opacity-60" : ""}`}>
          <div className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground mb-3">
            Step 1 — Select an eligible order
          </div>
          {!isTenantReady && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-3" data-testid="dispatch-console-order-lookup-locked">
              Locked until tenant readiness passes above.
            </p>
          )}
          <form onSubmit={runLookup} className="flex gap-2 items-end">
            <div className="flex-1">
              <Label htmlFor="dispatch-console-order-code-input" className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Order code
              </Label>
              <Input
                id="dispatch-console-order-code-input"
                value={orderCode}
                onChange={(e) => setOrderCode(e.target.value)}
                placeholder="UBE-ORD-000123"
                autoComplete="off"
                disabled={!isTenantReady}
                data-testid="dispatch-console-order-code-input"
              />
            </div>
            <Button
              type="submit"
              disabled={lookupLoading || !orderCode.trim() || !isTenantReady}
              data-testid="dispatch-console-lookup-btn"
            >
              <Search className="w-3.5 h-3.5 mr-1.5" />
              {lookupLoading ? "Looking up…" : "Look up"}
            </Button>
          </form>
          {lookupError && (
            <div className="mt-3 text-sm text-rose-700 flex items-center gap-2" role="alert" data-testid="dispatch-console-lookup-error">
              <AlertTriangle className="w-4 h-4" /> {lookupError}
            </div>
          )}

          {lookup && (
            <div className="mt-4 border rounded-lg p-4" data-testid="dispatch-console-order-summary">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[13px]">
                <SummaryField label="Order code" value={lookup.order_code} />
                <SummaryField label="Tenant" value={lookup.tenant_name} />
                <SummaryField label="Amount" value={`${lookup.amount ?? "—"} ${lookup.currency || ""}`.trim()} />
                <SummaryField label="LocalDelivery status" value={lookup.local_delivery_status} />
              </div>
              <div className="mt-3 pt-3 border-t border-dashed flex flex-wrap gap-2">
                <SummaryField
                  label="Quote expiry"
                  value={lookup.eligibility?.quote_expires_at
                    ? `${fmtRemaining(msRemaining(lookup.eligibility.quote_expires_at, nowMs))} remaining`
                    : "—"}
                />
              </div>
              <div className="mt-3 pt-3 border-t border-dashed flex flex-wrap gap-2">
                <EligibilityPresenceBadge value={lookup.eligibility?.pickup_contact_present} label="Pickup contact" />
                <EligibilityPresenceBadge value={lookup.eligibility?.dropoff_contact_present} label="Dropoff contact" />
                <EligibilityPresenceBadge value={lookup.eligibility?.coordinates_present?.pickup} label="Pickup coordinates" />
                <EligibilityPresenceBadge value={lookup.eligibility?.coordinates_present?.dropoff} label="Dropoff coordinates" />
                <PresenceBadge
                  ok={!lookup.existing_attempts?.has_provider_delivery_id}
                  label={lookup.existing_attempts?.has_provider_delivery_id ? "Provider delivery already exists" : "No provider delivery yet"}
                />
                <PresenceBadge
                  ok={!lookup.existing_attempts?.in_flight}
                  label={lookup.existing_attempts?.in_flight ? "Attempt in flight" : "No in-flight attempt"}
                />
              </div>
              {!lookup.eligibility?.eligible && (
                <div className="mt-3 pt-3 border-t border-dashed text-sm text-rose-700 flex items-center gap-2" data-testid="dispatch-console-ineligible">
                  <XCircle className="w-4 h-4" />
                  Not eligible ({lookup.eligibility?.code}): {lookup.eligibility?.message}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* -------- Step 2+3: attestation + preview -------- */}
        {lookup?.eligibility?.eligible && (
          <Card className="p-4">
            <div className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground mb-3">
              Step 2 — Credential TEST attestation, then Step 3 — Dispatch preview
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <Button
                variant="outline"
                onClick={runAttestation}
                disabled={attestationLoading}
                data-testid="dispatch-console-attest-btn"
              >
                {attestationLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                {attestation ? "Re-confirm TEST credential attestation" : "Confirm TEST credential attestation"}
              </Button>
              {attestation && (
                <span className="text-xs text-emerald-700 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Recorded at {attestation.verified_at}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2 max-w-2xl">
              Run this every attempt, unconditionally — it is safe and idempotent to re-run even if
              already active, and there is no reliable way to detect a missing attestation after the
              fact from this console alone.
            </p>

            <div className="mt-4 pt-4 border-t flex flex-wrap gap-2 items-center">
              <Button
                onClick={runPreview}
                disabled={previewLoading || !isTenantReady}
                data-testid="dispatch-console-preview-btn"
              >
                {previewLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                Run dispatch preview
              </Button>
              {!isTenantReady && (
                <span className="text-xs text-amber-800" data-testid="dispatch-console-preview-locked">
                  Locked — tenant readiness has not passed.
                </span>
              )}
              {previewError && (
                <span className="text-sm text-rose-700 flex items-center gap-2" role="alert">
                  <AlertTriangle className="w-4 h-4" /> {previewError}
                </span>
              )}
            </div>

            {preview && (
              <div className="mt-3 border rounded-lg p-3 text-[13px]" data-testid="dispatch-console-preview-summary">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <SummaryField
                    label="Quote expires in"
                    value={fmtRemaining(msRemaining(preview.quote_expires_at, nowMs))}
                  />
                  <SummaryField
                    label="Authorization expires in"
                    value={fmtRemaining(msRemaining(preview.authorization_expires_at, nowMs))}
                  />
                  <SummaryField label="Environment" value={preview.environment} />
                  <SummaryField label="Provider" value={preview.provider} />
                </div>
                <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  {preview.warning}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* -------- Step 4: explicit one-time confirmation -------- */}
        {lookup?.eligibility?.eligible && preview && (
          <Card className="p-4">
            <div className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground mb-3">
              Step 4 — Explicit one-time confirmation
            </div>
            {confirmResult ? (
              <div className="text-sm text-emerald-700 flex items-center gap-2" data-testid="dispatch-console-already-confirmed">
                <CheckCircle2 className="w-4 h-4" />
                Confirmation already sent for this preview. No retry is authorized — see Step 5 below
                for the real outcome.
              </div>
            ) : blockingReasons.length > 0 ? (
              <div className="space-y-1.5" data-testid="dispatch-console-blocking-reasons">
                {blockingReasons.map((r, i) => (
                  <div key={i} className="text-sm text-rose-700 flex items-center gap-2">
                    <XCircle className="w-4 h-4 shrink-0" /> {r}
                  </div>
                ))}
              </div>
            ) : (
              <Button
                variant="destructive"
                onClick={() => setConfirmDialogOpen(true)}
                data-testid="dispatch-console-open-confirm-btn"
              >
                Open confirmation — dispatch exactly once
              </Button>
            )}
          </Card>
        )}

        <Dialog open={confirmDialogOpen} onOpenChange={(o) => { if (!o) setConfirmDialogOpen(false); }}>
          <DialogContent data-testid="dispatch-console-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-destructive" /> Confirm dispatch — exactly once
              </DialogTitle>
              <DialogDescription>
                Confirming triggers the sole Uber TEST Create Delivery attempt for this order. No
                retry is authorized. This cannot be undone from this console.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={confirmAckChecked}
                  onChange={(e) => setConfirmAckChecked(e.target.checked)}
                  data-testid="dispatch-console-confirm-ack-checkbox"
                  className="mt-1"
                />
                <span>{AUTHORIZATION_ACK_TEXT}</span>
              </label>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Type the exact order code to confirm: <span className="font-mono">{lookup?.order_code}</span>
                </Label>
                <Input
                  value={confirmTypedOrderCode}
                  onChange={(e) => setConfirmTypedOrderCode(e.target.value)}
                  autoComplete="off"
                  data-testid="dispatch-console-confirm-order-code-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
              <Button
                variant="destructive"
                disabled={!canSendConfirm}
                onClick={runConfirm}
                data-testid="dispatch-console-send-confirm-btn"
              >
                {confirmLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                Confirm — dispatch exactly once
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* -------- Step 5: authoritative delivery detail -------- */}
        {(confirmResult || detail) && (
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground">
                Step 5 — Authoritative delivery detail
              </div>
              <Button variant="outline" size="sm" onClick={runDetail} disabled={detailLoading} data-testid="dispatch-console-refresh-detail-btn">
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${detailLoading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              This is the real record. The confirm response alone is never authoritative — it can look
              successful even on a non-dispatch outcome.
            </p>
            {detail ? (
              <div className="space-y-2 text-[13px]" data-testid="dispatch-console-detail">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <SummaryField label="local_delivery.status" value={detail.local_delivery?.status} />
                  <SummaryField label="Dispatch attempts" value={String((detail.dispatch_attempts || []).length)} />
                  <SummaryField label="provider_delivery_id" value={providerDeliveryId || "(none yet)"} />
                  <SummaryField label="Tracking URL" value={detail.local_delivery?.tracking_url ? "present" : "—"} />
                </div>
                {isTerminalStop && (
                  <div className="text-sm text-rose-700 flex items-center gap-2" data-testid="dispatch-console-terminal-stop">
                    <XCircle className="w-4 h-4" />
                    Terminal operator stop state — do not poll or retry. Preserve this evidence.
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">Loading…</div>
            )}
          </Card>
        )}

        {/* -------- Step 6: poll, gated on provider_delivery_id -------- */}
        {detail && (
          <Card className="p-4">
            <div className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground mb-3">
              Step 6 — Poll status (only once provider_delivery_id exists)
            </div>
            <Button
              onClick={runPoll}
              disabled={!canPoll || pollLoading}
              data-testid="dispatch-console-poll-btn"
            >
              {pollLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
              Poll Uber TEST status
            </Button>
            {!canPoll && (
              <p className="text-xs text-muted-foreground mt-2">
                {isTerminalStop
                  ? "Polling is disabled — this delivery is in a terminal stop state."
                  : "Polling is disabled until a provider_delivery_id has been persisted."}
              </p>
            )}
            {pollResult && (
              <div className="mt-3 border rounded-lg p-3 text-[13px]" data-testid="dispatch-console-poll-result">
                <SummaryField label="Native status" value={pollResult.native_status} />
                <SummaryField label="Normalized status" value={pollResult.normalized_status} />
              </div>
            )}
          </Card>
        )}

        {/* -------- Reconciliation-refund — stranded pre-hotfix orders (UBE-ORD-000017 incident-recovery follow-up) -------- */}
        <Card className="p-4" data-testid="dispatch-console-recon-card">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-medium text-muted-foreground mb-3">
            <ReceiptText className="w-3.5 h-3.5" /> Reconciliation refund — stranded pre-hotfix orders
          </div>
          <p className="text-xs text-muted-foreground mb-3 max-w-2xl">
            For a Local Delivery order stuck at <span className="font-mono">payment_status=
            reconciliation_required</span> from before the atomic dispatch/refund ownership
            correction existed. Issues exactly one Stripe TEST refund, reason{" "}
            <span className="font-mono">quote_expired_before_dispatch</span>, and never creates,
            requests, or contacts Uber. Not a general refund tool — every gate below is enforced
            authoritatively by the backend regardless of what this page shows.
          </p>
          <form onSubmit={runReconLookup} className="flex gap-2 items-end">
            <div className="flex-1">
              <Label htmlFor="dispatch-console-recon-order-code-input" className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Order code
              </Label>
              <Input
                id="dispatch-console-recon-order-code-input"
                value={reconOrderCode}
                onChange={(e) => setReconOrderCode(e.target.value)}
                placeholder="UBE-ORD-000017"
                autoComplete="off"
                data-testid="dispatch-console-recon-order-code-input"
              />
            </div>
            <Button
              type="submit"
              disabled={reconLookupLoading || !reconOrderCode.trim()}
              data-testid="dispatch-console-recon-lookup-btn"
            >
              <Search className="w-3.5 h-3.5 mr-1.5" />
              {reconLookupLoading ? "Looking up…" : "Look up"}
            </Button>
          </form>
          {reconLookupError && (
            <div className="mt-3 text-sm text-rose-700 flex items-center gap-2" role="alert" data-testid="dispatch-console-recon-lookup-error">
              <AlertTriangle className="w-4 h-4" /> {reconLookupError}
            </div>
          )}

          {reconLookup && (
            <div className="mt-4 border rounded-lg p-4" data-testid="dispatch-console-recon-summary">
              <div className="flex items-center gap-2 mb-3">
                <Badge
                  variant={reconLookup.environment_mode === "test" ? "outline" : "destructive"}
                  data-testid="dispatch-console-recon-env-badge"
                >
                  {reconLookup.environment_mode === "test" ? "TEST / SANDBOX" : `ENVIRONMENT: ${reconLookup.environment_mode || "unknown"}`}
                </Badge>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[13px]">
                <SummaryField label="Order code" value={reconLookup.order_code} />
                <SummaryField label="Tenant" value={reconLookup.tenant_name} />
                <SummaryField
                  label="Amount"
                  value={reconLookup.amount_cents != null
                    ? `${(reconLookup.amount_cents / 100).toFixed(2)} ${(reconLookup.currency || "").toUpperCase()}`
                    : "—"}
                />
                <SummaryField label="Reason" value={reconLookup.reason_code} />
              </div>

              {reconResult ? (
                <div className="mt-3 pt-3 border-t border-dashed" data-testid="dispatch-console-recon-result">
                  {reconResult.outcome === "succeeded" ? (
                    <div className="text-sm text-emerald-700 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Refunded — payment/order state is now truthfully <span className="font-mono mx-1">refunded</span>.
                      Same-order recovery eligibility is available through the existing status page.
                    </div>
                  ) : reconResult.outcome === "failed" ? (
                    <div className="text-sm text-rose-700 flex items-center gap-2">
                      <XCircle className="w-4 h-4" /> Refund definitively failed. Order remains reconciliation_required.
                    </div>
                  ) : (
                    <div className="text-sm text-amber-800 flex items-center gap-2" role="alert">
                      <AlertTriangle className="w-4 h-4" />
                      Provider result was ambiguous — verify directly in the Stripe Dashboard before any
                      further action. Do not resubmit.
                    </div>
                  )}
                </div>
              ) : reconOutcome === "ambiguous" ? (
                <div className="mt-3 pt-3 border-t border-dashed text-sm text-amber-800 flex items-center gap-2" role="alert" data-testid="dispatch-console-recon-ambiguous">
                  <AlertTriangle className="w-4 h-4" /> Result unclear — checking authoritative order state now. Do not retry.
                </div>
              ) : reconOutcome === "unresolved" ? (
                <div className="mt-3 pt-3 border-t border-dashed text-sm text-rose-700 flex items-center gap-2 flex-wrap" role="alert" data-testid="dispatch-console-recon-unresolved">
                  <AlertTriangle className="w-4 h-4" /> Outcome could not be confirmed. Do not retry — verify in the
                  Stripe Dashboard, then refresh manually before doing anything else.
                  <Button variant="outline" size="sm" onClick={() => refreshReconLookup()} data-testid="dispatch-console-recon-manual-refresh-btn">
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
                  </Button>
                </div>
              ) : reconBlockingReasons.length > 0 ? (
                <div className="mt-3 pt-3 border-t border-dashed space-y-1" data-testid="dispatch-console-recon-blocking-reasons">
                  {reconBlockingReasons.map((r, i) => (
                    <div key={i} className="text-sm text-rose-700 flex items-center gap-2">
                      <XCircle className="w-4 h-4 shrink-0" /> {r}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-3 pt-3 border-t border-dashed">
                  <Button
                    variant="destructive"
                    onClick={() => setReconDialogOpen(true)}
                    data-testid="dispatch-console-recon-open-btn"
                  >
                    <ReceiptText className="w-3.5 h-3.5 mr-1.5" /> Refund — exactly once
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>

        <Dialog open={reconDialogOpen} onOpenChange={(o) => { if (!o) setReconDialogOpen(false); }}>
          <DialogContent data-testid="dispatch-console-recon-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-destructive" /> Confirm reconciliation refund — {reconLookup?.order_code}
              </DialogTitle>
              <DialogDescription>
                Tenant <span className="font-mono">{reconLookup?.tenant_name}</span>, order{" "}
                <span className="font-mono">{reconLookup?.order_code}</span>,{" "}
                <span className="font-mono">
                  {reconLookup?.amount_cents != null ? `${(reconLookup.amount_cents / 100).toFixed(2)} ${(reconLookup.currency || "").toUpperCase()}` : "—"}
                </span>, reason <span className="font-mono">{reconLookup?.reason_code}</span>, environment{" "}
                <span className="font-mono">{reconLookup?.environment_mode}</span>. This issues exactly one
                Stripe TEST refund. No Uber delivery will be created. No retry is authorized.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={reconAckChecked}
                  onChange={(e) => setReconAckChecked(e.target.checked)}
                  data-testid="dispatch-console-recon-ack-checkbox"
                  className="mt-1"
                />
                <span>{RECONCILIATION_REFUND_CONFIRMATION_TEXT}</span>
              </label>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Type the exact order code to confirm: <span className="font-mono">{reconLookup?.order_code}</span>
                </Label>
                <Input
                  value={reconTypedOrderCode}
                  onChange={(e) => setReconTypedOrderCode(e.target.value)}
                  autoComplete="off"
                  data-testid="dispatch-console-recon-typed-order-code-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReconDialogOpen(false)}>Cancel</Button>
              <Button
                variant="destructive"
                disabled={!canSendRecon}
                onClick={runReconRefund}
                data-testid="dispatch-console-recon-send-btn"
              >
                {reconSubmitting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                Refund — exactly once
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </InternalShell>
  );
}

function SummaryField({ label, value }) {
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-medium text-foreground truncate" title={String(value ?? "—")}>
        {value ?? "—"}
      </div>
    </div>
  );
}
